// Admin TanStack-Query helpers. Keys are arrays per the React-Query
// convention; the leading string is the resource family for
// invalidation.
//
// Paginated list fetchers normalize the wire envelope
// `{ <items_field>: [...], page: PageInfo }` into Paginated<T> with
// items always under `items` so consumers don't have to know the
// per-endpoint field name.
//
// Sort + filter parameters: each fetcher accepts an opts object that
// carries the page-size / offset, plus per-endpoint filter columns and
// an optional `sort: SortState`. Sort flattens to `?sort_by=&sort_dir=`;
// the gateway validates the column name against a whitelist per
// endpoint, so a typo here surfaces as a 400 rather than silently
// going unsorted. Cache keys include every filter/sort value so each
// combination gets its own React-Query entry.
import type { SortState } from "@/components/ui/SortableHeader";
import { api, qs } from "./api";
import type { CurrencyCode } from "./currency";
import type {
  AdminMe,
  MyPermissions,
  AdminEntity,
  ChangeMyPasswordInput,
  ChangePasswordResult,
  SetAdminPasswordResult,
  AdminWithRoles,
  RoleSummary,
  PermissionItem,
  MFAFactor,
  EnrollMFAResult,
  AuditEvent,
  CategoryItem,
  EventItem,
  EventModeStatus,
  EventOrderCriterion,
  EventOrderingConfig,
  EventOrderMode,
  LanguageItem,
  MarketItem,
  MarketOutcome,
  UserItem,
  AdminComment,
  UserDetailResponse,
  UserLoginEvent,
  UserLoginListResponse,
  WalletItem,
  WalletTransferResult,
  SystemAccount,
  SystemTransferResult,
  FundLinkedMMResult,
  ReturnLinkedMMResult,
  LinkedMMAccount,
  SystemAccountAdjustResult,
  WalletTransaction,
  WalletDeposit,
  WalletReservation,
  WalletWithdrawal,
  PositionItem,
  OrderItem,
  SettlementTradeItem,
  PeerInfo,
  OwnershipLease,
  EngineStatus,
  RebalanceResult,
  SystemConfigItem,
  DashboardOverview,
  TickerSnapshot,
  TradePrint,
  PageInfo,
  Paginated,
  IgnoreEntry,
  RankingBrokerVisibility,
  RankBoard,
  AdminLeague,
  AdminLeagueDetail,
  TradingChannelItem,
  TradingCapabilityItem,
} from "./types";

// PageOpts is the shape every paginated fetcher takes. q (search)
// and status (filter) are endpoint-specific and ignored when not
// supplied.
export interface PageOpts {
  limit: number;
  offset: number;
}

// Helper — flatten a SortState into the gateway's wire params. Returns
// `{}` (i.e. sort_by/sort_dir are skipped by qs()) when no field is
// selected, so the server falls back to its default ordering.
function sortQS(sort?: SortState) {
  if (!sort?.by) return { sort_by: undefined, sort_dir: undefined };
  return { sort_by: sort.by, sort_dir: sort.dir };
}

// sortKey — stable string for cache keys. We collapse to a single
// string so the keys array doesn't grow unbounded.
function sortKey(sort?: SortState): string {
  if (!sort?.by) return "";
  return `${sort.by}:${sort.dir}`;
}

export const keys = {
  auth: ["admin-auth"] as const,
  me: () => [...keys.auth, "me"] as const,
  myPerms: () => [...keys.auth, "my-perms"] as const,
  myFactors: () => [...keys.auth, "my-factors"] as const,

  dashboard: ["admin-dashboard"] as const,

  // Per-market trading channels (SM-446). Keyed by the LOGICAL market
  // id — one entry holds both modes' rows, so enabling GAME invalidates
  // exactly one key.
  marketChannels: (marketID: string) => ["admin-market-channels", marketID] as const,

  audit: ["admin-audit"] as const,
  auditList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.audit,
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,

  admins: ["admin-admins"] as const,
  adminsList: (
    q: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.admins,
      "list",
      q ?? "",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  adminDetail: (id: string) => [...keys.admins, "detail", id] as const,
  roles: ["admin-roles"] as const,
  rolesList: (limit: number, offset: number, sort?: SortState) =>
    [...keys.roles, "list", limit, offset, sortKey(sort)] as const,
  permissions: ["admin-permissions"] as const,
  permissionsList: (limit: number, offset: number) =>
    [...keys.permissions, "list", limit, offset] as const,

  campaigns: ["admin-campaigns"] as const,
  campaignsList: (
    q: string | undefined,
    status: string | undefined,
    delivery: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
  ) =>
    [
      ...keys.campaigns,
      "list",
      q ?? "",
      status ?? "",
      delivery ?? "",
      limit,
      offset,
      sortKey(sort),
    ] as const,
  campaignDetail: (id: string) => [...keys.campaigns, "detail", id] as const,
  campaignsByFunding: (code: string) => [...keys.campaigns, "by-funding", code] as const,
  campaignRecipients: (id: string, limit: number, offset: number) =>
    [...keys.campaigns, "recipients", id, limit, offset] as const,
  campaignBlacklist: (id: string) => [...keys.campaigns, "blacklist", id] as const,
  campaignClaims: (id: string, result: string, limit: number, offset: number) =>
    [...keys.campaigns, "claims", id, result, limit, offset] as const,

  catalog: ["admin-catalog"] as const,
  categories: () => [...keys.catalog, "categories"] as const,
  categoriesList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
    q?: string,
  ) =>
    [
      ...keys.catalog,
      "categories",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
      q ?? "",
    ] as const,
  topics: () => [...keys.catalog, "events"] as const,
  topicsList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
    q?: string,
  ) =>
    [
      ...keys.catalog,
      "events",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
      q ?? "",
    ] as const,
  rules: (status?: string) =>
    [...keys.catalog, "markets", status ?? "all"] as const,
  rulesList: (
    status: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
    q?: string,
  ) =>
    [
      ...keys.catalog,
      "markets",
      "list",
      status ?? "all",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
      q ?? "",
    ] as const,
  ruleDetail: (id: string) => [...keys.catalog, "rule", id] as const,
  categoryDetail: (id: string) => [...keys.catalog, "category", id] as const,
  topicDetail: (id: string) => [...keys.catalog, "topic", id] as const,

  // Language registry + per-entity catalog translations (SM-283).
  // Translations sit under the catalog family on purpose: the coarse
  // keys.catalog invalidation every catalog mutation fires also
  // refreshes the synthesized `en` row after a Details save.
  languages: ["admin-languages"] as const,
  translations: (kind: TranslationEntityKind, id: string) =>
    [...keys.catalog, "translations", kind, id] as const,

  // Public event ordering (SM-355). Its own family, like languages —
  // catalog mutations don't change the ordering CONFIG. The preview is
  // keyed on the draft parameters so each draft shape caches its rows.
  eventOrdering: ["admin-event-ordering"] as const,
  eventOrderingPreview: (criteria: string, pinnedFirst: boolean, limit: number) =>
    [...keys.eventOrdering, "preview", criteria, pinnedFirst, limit] as const,
  eventOrderingCatalog: (limit: number) =>
    [...keys.eventOrdering, "catalog", limit] as const,

  users: ["admin-users"] as const,
  usersList: (
    q: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.users,
      "list",
      q ?? "",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  userDetail: (id: string) => [...keys.users, "detail", id] as const,
  // The Login log card's own read (SM-821), a cache entry per page.
  userLogins: (id: string, limit: number, offset: number) =>
    [...keys.users, "logins", id, limit, offset] as const,

  // Comment moderation is scoped per market group (event_id when grouped,
  // market_id when loose) — the same handle the trader client routes on.
  comments: (groupID: string) => ["admin-comments", groupID] as const,
  commentsList: (groupID: string, limit: number, offset: number) =>
    ["admin-comments", groupID, "list", limit, offset] as const,

  treasury: ["admin-treasury"] as const,
  wallets: () => [...keys.treasury, "wallets"] as const,
  walletsList: (
    q: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.treasury,
      "wallets",
      "list",
      q ?? "",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  // The currency is part of the key: a user's GOLD and RED wallets are
  // different balances, and one key would cache whichever loaded last.
  walletDetail: (id: string, currency?: string) =>
    [...keys.treasury, "wallet", id, currency ?? "GOLD"] as const,
  /** Prefix over ALL of one user's wallets — for invalidation after an
   *  action whose blast radius is the user, not one currency. */
  walletDetailAll: (id: string) => [...keys.treasury, "wallet", id] as const,
  walletBalances: (id: string) => [...keys.treasury, "wallet", id, "balances"] as const,
  walletTxns: (
    id: string,
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.treasury,
      "wallet",
      id,
      "transactions",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  walletDeposits: (id: string, limit: number, offset: number) =>
    [...keys.treasury, "wallet", id, "deposits", limit, offset] as const,
  walletWithdrawals: (id: string, limit: number, offset: number) =>
    [...keys.treasury, "wallet", id, "withdrawals", limit, offset] as const,
  walletReservations: (id: string, limit: number) =>
    [...keys.treasury, "wallet", id, "reservations", limit] as const,

  // System accounts (SM-152). Nested under the treasury family so the
  // blanket keys.treasury invalidation after any money move refreshes
  // the tree + details too.
  systemAccounts: () => [...keys.treasury, "system-accounts"] as const,
  // The currency is part of the key because it is part of the account's
  // IDENTITY (SM-414): GOLD's SYSTEM_TREASURY and RED's are different
  // accounts with different balances, and caching them under one key
  // would show whichever was fetched last.
  systemAccountDetail: (code: string, currency: string) =>
    [...keys.treasury, "system-account", code, currency] as const,
  systemAccountTxns: (code: string, currency: string, limit: number, offset: number) =>
    [...keys.treasury, "system-account", code, currency, "transactions", limit, offset] as const,
  // Linked MM reserve (SM-379). Also under the treasury family, so a
  // keys.treasury invalidation after funding refreshes the reserve
  // balance, the execution balance, and the history together — they
  // all changed in the same transaction.
  systemAccountLinkedMM: (code: string) =>
    [...keys.treasury, "system-account", code, "linked-mm"] as const,
  campaignFundingOptions: () =>
    [...keys.campaigns, "funding-options"] as const,

  trading: ["admin-trading"] as const,
  tradingCapabilities: () => [...keys.trading, "capabilities"] as const,
  positions: () => [...keys.trading, "positions"] as const,
  positionsList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.trading,
      "positions",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  orders: () => [...keys.trading, "orders"] as const,
  ordersList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.trading,
      "orders",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  orderHistory: () => [...keys.trading, "order-history"] as const,
  orderHistoryList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.trading,
      "order-history",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  tradeHistory: () => [...keys.trading, "trade-history"] as const,
  tradeHistoryList: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: Record<string, string | undefined>,
  ) =>
    [
      ...keys.trading,
      "trade-history",
      "list",
      limit,
      offset,
      sortKey(sort),
      filters ?? {},
    ] as const,
  tickers: () => [...keys.trading, "tickers"] as const,
  trades: (instrumentID: string) => [...keys.trading, "trades", instrumentID] as const,

  engine: ["admin-engine"] as const,
  status: () => [...keys.engine, "status"] as const,
  peers: () => [...keys.engine, "peers"] as const,
  ownership: () => [...keys.engine, "ownership"] as const,

  configs: () => ["admin-configs"] as const,
  configsList: (limit: number, offset: number, sort?: SortState) =>
    ["admin-configs", "list", limit, offset, sortKey(sort)] as const,

  // ranking / leagues (ranking-service via backoffice proxy)
  ranking: ["admin-ranking"] as const,
  ignoreList: () => [...keys.ranking, "ignore"] as const,
  // Under keys.ranking so a ranking mutation's invalidation reaches it.
  rankBrokers: () => [...keys.ranking, "brokers"] as const,
  globalRank: (metric: string, limit: number, offset: number) =>
    [...keys.ranking, "global", metric, limit, offset] as const,

  leagues: ["admin-leagues"] as const,
  leaguesList: (
    q: string | undefined,
    status: string | undefined,
    limit: number,
    offset: number,
  ) => [...keys.leagues, "list", q ?? "", status ?? "", limit, offset] as const,
  leagueDetail: (id: string, metric: string, limit: number, offset: number) =>
    [...keys.leagues, "detail", id, metric, limit, offset] as const,
};

// --- typed fetchers ---

export const fetchMe = () => api<AdminMe>("/admin/me");
export const fetchMyPermissions = () =>
  api<MyPermissions>("/admin/me/permissions");

export const fetchDashboard = () => api<DashboardOverview>("/admin/dashboard");

export const fetchAudit = (
  opts: PageOpts & {
    sort?: SortState;
    actor_kind?: string;
    event_type?: string;
    severity?: string;
    // Exact-match target filters (SM-152): e.g. target_kind
    // "system_account" + target_id the account_code scope the log to
    // one treasury account's history.
    target_kind?: string;
    target_id?: string;
    date_from?: string;
    date_to?: string;
  },
) =>
  api<{ events: AuditEvent[]; page: PageInfo }>(
    `/admin/audit${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      actor_kind: opts.actor_kind,
      event_type: opts.event_type,
      severity: opts.severity,
      target_kind: opts.target_kind,
      target_id: opts.target_id,
      date_from: opts.date_from,
      date_to: opts.date_to,
    })}`,
  ).then<Paginated<AuditEvent>>((r) => ({ items: r.events ?? [], page: r.page }));

export const fetchAdmins = (
  opts: PageOpts & {
    q?: string;
    sort?: SortState;
    status?: string;
    role?: string;
  },
) =>
  api<{ admins: AdminEntity[]; page: PageInfo }>(
    `/admin/admins${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: opts.q,
      ...sortQS(opts.sort),
      status: opts.status,
      role: opts.role,
    })}`,
  ).then<Paginated<AdminEntity>>((r) => ({ items: r.admins ?? [], page: r.page }));
export const fetchAdmin = (id: string) =>
  api<AdminWithRoles>(`/admin/admins/${id}`);
export const fetchRoles = (opts: PageOpts & { sort?: SortState }) =>
  api<{ roles: RoleSummary[]; page: PageInfo }>(
    `/admin/roles${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
    })}`,
  ).then<Paginated<RoleSummary>>((r) => ({ items: r.roles ?? [], page: r.page }));
export const fetchPermissions = (opts: PageOpts) =>
  api<{ permissions: PermissionItem[]; page: PageInfo }>(
    `/admin/permissions${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then<Paginated<PermissionItem>>((r) => ({
    items: r.permissions ?? [],
    page: r.page,
  }));

export const fetchMyFactors = () =>
  api<{ factors: MFAFactor[] }>("/admin/me/mfa/factors").then((r) => r.factors ?? []);

export const fetchCategories = (
  opts: PageOpts & { sort?: SortState; include_archived?: string; q?: string },
) =>
  api<{ categories: CategoryItem[]; page: PageInfo }>(
    `/admin/catalog/categories${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      include_archived: opts.include_archived,
      q: opts.q,
    })}`,
  ).then<Paginated<CategoryItem>>((r) => ({
    items: r.categories ?? [],
    page: r.page,
  }));

// Recursive tree shape returned by /admin/catalog/categories/tree.
// Matches market-service's nodeResp — every level is a CategoryItem
// plus a children array (possibly empty).
export interface CategoryTreeNode extends CategoryItem {
  children?: CategoryTreeNode[];
}
export const fetchCategoryTree = (opts: { includeArchived?: boolean } = {}) =>
  api<{ tree: CategoryTreeNode[] }>(
    `/admin/catalog/categories/tree${qs({
      include_archived: opts.includeArchived ? "true" : undefined,
    })}`,
  );
export const fetchTopics = (
  opts: PageOpts & {
    sort?: SortState;
    status?: string;
    category_id?: string;
    include_archived?: string;
    // Free-text search against event name / event_id. Backend
    // matches the same set the public catalog uses (market-service
    // listTopics handler — r.URL.Query().Get("q")). Empty / omitted →
    // no filter.
    q?: string;
  },
) =>
  api<{ events: EventItem[]; page: PageInfo }>(
    `/admin/catalog/topics${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      category_id: opts.category_id,
      include_archived: opts.include_archived,
      q: opts.q,
    })}`,
  ).then<Paginated<EventItem>>((r) => ({ items: r.events ?? [], page: r.page }));
export const fetchRules = (
  opts: PageOpts & {
    sort?: SortState;
    status?: string;
    category_id?: string;
    event_id?: string;
    include_test?: string;
    // Free-text search against market_id / name. Backend matches the
    // same set of columns as the public catalog (see market-service
    // listRules handler — r.URL.Query().Get("q")). Empty / omitted →
    // no filter.
    q?: string;
  },
) =>
  api<{ markets: MarketItem[]; page: PageInfo }>(
    `/admin/catalog/rules${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      category_id: opts.category_id,
      event_id: opts.event_id,
      include_test: opts.include_test,
      q: opts.q,
    })}`,
  ).then<Paginated<MarketItem>>((r) => ({ items: r.markets ?? [], page: r.page }));
export const fetchRule = (id: string) => api<MarketItem>(`/admin/catalog/rules/${id}`);

/**
 * A market's trading channels (SM-403/SM-426, proxied by
 * backoffice-service to market-service).
 *
 * The CASH channel always exists and its id IS the market id — the
 * legacy identity, preserved so every pre-epic order, position and
 * stream key still resolves. A GAME row exists only once GAME has been
 * enabled for that market at least once, so an empty GAME slot means
 * "never enabled", not "disabled".
 *
 * `effective_enabled` / `effective_published` are the SERVER's verdicts,
 * folding the event flag and the F5 visibility flag. The console renders
 * those rather than recomputing them: the F5 state is not otherwise
 * visible here, so arithmetic done in the browser would be wrong in
 * exactly the case operators care about.
 */
export const fetchMarketChannels = (marketID: string) =>
  api<{ channels: TradingChannelItem[] }>(
    `/admin/catalog/rules/${marketID}/channels`,
  ).then<TradingChannelItem[]>((r) => r.channels ?? []);

/** The order-service write-path gate, forwarded verbatim by
 * backoffice-service. This must not be inferred from channel visibility. */
export const fetchTradingCapabilities = () =>
  api<{ capabilities: TradingCapabilityItem[] }>("/admin/trading/capabilities");

export const fetchUsers = (
  opts: PageOpts & {
    q?: string;
    sort?: SortState;
    status?: string;
    kyc_level?: string;
    role?: string;
  },
) =>
  api<{ users: UserItem[]; page: PageInfo }>(
    `/admin/users${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: opts.q,
      ...sortQS(opts.sort),
      status: opts.status,
      kyc_level: opts.kyc_level,
      role: opts.role,
    })}`,
  ).then<Paginated<UserItem>>((r) => ({ items: r.users ?? [], page: r.page }));
export const fetchUserDetail = (id: string) =>
  api<UserDetailResponse>(`/admin/users/${id}`);
// One user's accepted logins, newest first (SM-821): client-service's
// LOGIN_SUCCEEDED auth events through backoffice (contracts
// BackofficeUserLoginsPathFmt), which pins the event type — this read can
// only ever page through logins. The wire list key is `entries`
// (contracts AuthEventListResponse).
export const fetchUserLogins = (id: string, opts: PageOpts) =>
  api<UserLoginListResponse>(
    `/admin/users/${id}/logins${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then<Paginated<UserLoginEvent>>((r) => ({ items: r.entries ?? [], page: r.page }));

export const fetchWallets = (
  opts: PageOpts & {
    q?: string;
    sort?: SortState;
    status?: string;
    currency?: string;
  },
) =>
  api<{ wallets: WalletItem[]; page: PageInfo }>(
    `/admin/wallets${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: opts.q,
      ...sortQS(opts.sort),
      status: opts.status,
      currency: opts.currency,
    })}`,
  ).then<Paginated<WalletItem>>((r) => ({ items: r.wallets ?? [], page: r.page }));
// A user holds one wallet per currency (ledger 0025). Absent = GOLD, the
// same default ledger applies — so a caller that does not care is unchanged.
export const fetchWallet = (userID: string, currency?: string) =>
  api<WalletItem>(`/admin/wallets/${userID}${qs({ currency })}`);

/**
 * Every currency one user holds, in one read.
 *
 * A READ, not an ensure: absent wallets come back as true zeros rather than
 * being materialised, so opening a page never authors ledger state. Answers
 * the question the per-currency page cannot — "what does this person hold at
 * all" — which navigating three times tends to get wrong by omission.
 */
export const fetchWalletBalances = (userID: string) =>
  api<{ balances: WalletBalanceRow[] }>(`/admin/wallets/${userID}/balances`).then(
    (r) => r.balances ?? [],
  );

export interface WalletBalanceRow {
  currency: string;
  /** Empty when the user has no wallet in this currency — a true zero. */
  wallet_id?: string;
  status?: string;
  available: string;
  reserved: string;
  pending_withdrawal: string;
  updated_at?: string;
}

// fetchWalletTransactions / fetchWalletDeposits / fetchWalletWithdrawals
// drive the wallet detail page's history sections. Each is a thin
// backoffice proxy onto ledger's per-user public endpoints, scoped by
// the wallet's user_id.
export const fetchWalletTransactions = (
  userID: string,
  opts: PageOpts & {
    sort?: SortState;
    kind?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
  },
) =>
  api<{ transactions: WalletTransaction[]; page: PageInfo }>(
    `/admin/wallets/${userID}/transactions${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      kind: opts.kind,
      status: opts.status,
      date_from: opts.date_from,
      date_to: opts.date_to,
    })}`,
  ).then<Paginated<WalletTransaction>>((r) => ({
    items: r.transactions ?? [],
    page: r.page,
  }));
export const fetchWalletDeposits = (
  userID: string,
  opts: PageOpts & { sort?: SortState; status?: string; provider?: string },
) =>
  api<{ deposits: WalletDeposit[]; page: PageInfo }>(
    `/admin/wallets/${userID}/deposits${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      provider: opts.provider,
    })}`,
  ).then<Paginated<WalletDeposit>>((r) => ({
    items: r.deposits ?? [],
    page: r.page,
  }));
export const fetchWalletWithdrawals = (
  userID: string,
  opts: PageOpts & { sort?: SortState; status?: string; provider?: string },
) =>
  api<{ withdrawals: WalletWithdrawal[]; page: PageInfo }>(
    `/admin/wallets/${userID}/withdrawals${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      provider: opts.provider,
    })}`,
  ).then<Paginated<WalletWithdrawal>>((r) => ({
    items: r.withdrawals ?? [],
    page: r.page,
  }));

// fetchWalletReservations powers the per-user "Reserved" breakdown
// on the wallet detail page. The ledger admin endpoint is capped (no
// offset pagination today); items.length === limit signals "saturated,
// narrow with a market filter once that lands."
export const fetchWalletReservations = (
  userID: string,
  opts: { limit: number },
) =>
  api<{ reservations: WalletReservation[]; page: PageInfo }>(
    `/admin/wallets/${userID}/reservations${qs({
      limit: opts.limit,
    })}`,
  ).then<Paginated<WalletReservation>>((r) => ({
    items: r.reservations ?? [],
    page: r.page,
  }));

// --- system accounts (SM-152 treasury) ---
//
// Thin proxies onto ledger /admin/system-accounts/* via backoffice.
// Account codes can contain ":" or "." (children are PARENT.SUFFIX),
// so every path segment goes through encodeURIComponent. Amounts are
// public 4dp minor-unit strings (parseSparkToMinor output). The
// deposit / withdraw / transfer mutations take an operationID — the
// per-confirm idempotency key (crypto.randomUUID()): a retried submit
// with the same key replays the original move server-side instead of
// minting/moving the money twice.

export const fetchSystemAccounts = () =>
  api<{ accounts: SystemAccount[] }>("/admin/system-accounts").then(
    (r) => r.accounts ?? [],
  );
// Every /admin/system-accounts/{code} call carries ?currency=, because the
// code alone names two or three rows since ledger migration 0025. Ledger
// defaults an ABSENT currency to GOLD (G3), so omitting it silently returns
// the GOLD row — which is exactly how this console ended up showing GOLD's
// balances on every currency's page.
export const fetchSystemAccount = (code: string, currency: string) =>
  api<SystemAccount>(
    `/admin/system-accounts/${encodeURIComponent(code)}${qs({ currency })}`,
  );
export const fetchSystemAccountTransactions = (
  code: string,
  currency: string,
  opts: PageOpts,
) =>
  api<{ transactions: WalletTransaction[]; page: PageInfo }>(
    `/admin/system-accounts/${encodeURIComponent(code)}/transactions${qs({
      currency,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<Paginated<WalletTransaction>>((r) => ({
    items: r.transactions ?? [],
    page: r.page,
  }));

// --- linked market-maker reserve (SM-342/SM-379) ---
//
// Backoffice does NOT resolve the user <-> reserve mapping; it asks
// market-maker through the backoffice proxy. That is deliberate: the
// mapping has exactly one owner, and a second implementation here
// would eventually disagree with the first about whose wallet a
// reserve pays.

export const fetchLinkedMMAccount = (code: string) =>
  api<LinkedMMAccount>(
    `/admin/system-accounts/${encodeURIComponent(code)}/linked-mm`,
  );

/**
 * Fund the linked execution wallet from this reserve.
 *
 * Note what is NOT a parameter: the destination. The server resolves it
 * from the persisted link, so this call cannot be pointed at another
 * user's wallet. `operationID` is the per-confirm idempotency key — a
 * retry with the same key replays the original transfer rather than
 * moving the money twice.
 */
export const fundLinkedMMAccount = (
  code: string,
  amountMinor: number,
  reason: string,
  operationID: string,
) =>
  api<FundLinkedMMResult>(
    `/admin/system-accounts/${encodeURIComponent(code)}/linked-mm/fund`,
    {
      method: "POST",
      body: { amount_minor: amountMinor, reason, idempotency_key: operationID },
    },
  );

/** Return available execution balance to this account's linked reserve. */
export const returnLinkedMMAccount = (
  code: string,
  amountMinor: number,
  reason: string,
  operationID: string,
) =>
  api<ReturnLinkedMMResult>(
    `/admin/system-accounts/${encodeURIComponent(code)}/linked-mm/return`,
    {
      method: "POST",
      body: { amount_minor: amountMinor, reason, idempotency_key: operationID },
    },
  );

export interface CreateSystemAccountInput {
  account_code: string;
  account_name: string;
  parent_account_code: string;
  purpose?: string;
  /**
   * Which currency the new account's wallet holds (SM-500). The ledger
   * defaults an ABSENT currency to GOLD (G3), so a child created under
   * a RED or GREEN parent used to come back GOLD — a real account, in
   * the wrong currency, filed under a parent holding none of its money.
   * The caller always knows the parent row it is adding to, so it says so.
   */
  currency: string;
}
export const createSystemAccount = (input: CreateSystemAccountInput) =>
  api<SystemAccount>("/admin/system-accounts", { method: "POST", body: input });

export const depositSystemAccount = (
  code: string,
  currency: string,
  amountMinor: string,
  reason: string,
  operationID: string,
) =>
  api<SystemAccountAdjustResult>(
    `/admin/system-accounts/${encodeURIComponent(code)}/deposit${qs({ currency })}`,
    {
      method: "POST",
      body: { operation_id: operationID, amount: amountMinor, reason },
    },
  );
export const withdrawSystemAccount = (
  code: string,
  currency: string,
  amountMinor: string,
  reason: string,
  operationID: string,
) =>
  api<SystemAccountAdjustResult>(
    `/admin/system-accounts/${encodeURIComponent(code)}/withdraw${qs({ currency })}`,
    {
      method: "POST",
      body: { operation_id: operationID, amount: amountMinor, reason },
    },
  );

// transferSystemAccount moves balance system→system, PLATFORM_REVENUE→
// system (revenue allocation), or system→user (funding) in ONE atomic
// ledger transaction. Which permission the server demands follows the
// direction: PLATFORM_REVENUE source → SYSTEM_ACCOUNT_ALLOCATE_REVENUE,
// user destination → SYSTEM_ACCOUNT_FUND_USER, else SYSTEM_ACCOUNT_TRANSFER.
export interface SystemTransferInput {
  fromCode: string;
  /** Exactly one of toCode / toUserID. */
  toCode?: string;
  toUserID?: string;
  amountMinor: string;
  reason: string;
  operationID: string;
  /**
   * The SINGLE currency both legs move in (SM-500). A transfer is
   * always same-currency — moving value between two currencies is a
   * conversion at a rate, and no operator form has one — so this is
   * the source ROW's currency, not a separate choice.
   *
   * Required rather than optional: the ledger reads an absent currency
   * as GOLD, so leaving it off did not fail, it moved the WRONG money.
   * An operator on the RED treasury's page pressing Transfer debited
   * the GOLD treasury, and nothing on the screen said so.
   */
  currency: string;
}
export const transferSystemAccount = (input: SystemTransferInput) =>
  api<SystemTransferResult>("/admin/system-accounts/transfer", {
    method: "POST",
    body: {
      operation_id: input.operationID,
      source: { account_code: input.fromCode },
      destination: input.toUserID
        ? { user_id: input.toUserID }
        : { account_code: input.toCode },
      amount: input.amountMinor,
      reason: input.reason,
      currency: input.currency,
    },
  });

export const freezeSystemAccount = (code: string, currency: string, reason: string) =>
  api(`/admin/system-accounts/${encodeURIComponent(code)}/freeze${qs({ currency })}`, {
    method: "POST",
    body: { reason },
  });
export const unfreezeSystemAccount = (code: string, currency: string, reason: string) =>
  api(`/admin/system-accounts/${encodeURIComponent(code)}/unfreeze${qs({ currency })}`, {
    method: "POST",
    body: { reason },
  });
export const closeSystemAccount = (code: string, currency: string, reason: string) =>
  api(`/admin/system-accounts/${encodeURIComponent(code)}/close${qs({ currency })}`, {
    method: "POST",
    body: { reason },
  });

// market_id accepts a comma-joined list as well as a single id on every
// Activity read: filtering by EVENT resolves to that event's member
// markets client-side (see useEventMarketScope), and every upstream
// list handler parses the repeated/comma form into a `market_id = ANY()`
// predicate.
export const fetchPositions = (
  opts: PageOpts & {
    sort?: SortState;
    status?: string;
    direction?: string;
    market_id?: string;
    user_id?: string;
    // The currency the exposure was STAKED in (SM-490) — the channel
    // policy's debit leg, which settlement resolves to a predicate on
    // its indexed trading_mode column. Nothing is staked in GREEN, and
    // upstream answers that with an empty page rather than every row.
    currency?: string;
  },
) =>
  api<{ positions: PositionItem[]; page: PageInfo }>(
    `/admin/positions${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      direction: opts.direction,
      market_id: opts.market_id,
      user_id: opts.user_id,
      currency: opts.currency,
    })}`,
  ).then<Paginated<PositionItem>>((r) => ({
    items: r.positions ?? [],
    page: r.page,
  }));
export const fetchOrders = (
  opts: PageOpts & {
    sort?: SortState;
    status?: string;
    side?: string;
    market_id?: string;
    user_id?: string;
    // The BOOK currency (SM-511) — orders.debit_currency, the channel
    // policy snapshotted at placement, which is the same column each
    // row's chip is drawn from. Nothing is debited in GREEN, and
    // upstream answers that with an empty page rather than every row.
    currency?: string;
  },
) =>
  api<{ orders: OrderItem[]; page: PageInfo }>(
    `/admin/orders${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      side: opts.side,
      market_id: opts.market_id,
      user_id: opts.user_id,
      currency: opts.currency,
    })}`,
  ).then<Paginated<OrderItem>>((r) => ({ items: r.orders ?? [], page: r.page }));
// The complete order ledger — every status, active and terminal. Same
// row shape as fetchOrders plus `reason` / `occurred_at`; side and price
// are INTENT space on this endpoint (see OrderItem).
export const fetchOrderHistory = (
  opts: PageOpts & {
    sort?: SortState;
    status?: string;
    side?: string;
    market_id?: string;
    user_id?: string;
    // Same column and the same contract as fetchOrders (SM-511), and it
    // reaches the terminal half too: order_history keeps the placement
    // snapshot, so a canceled GAME order is still a RED row.
    currency?: string;
  },
) =>
  api<{ orders: OrderItem[]; page: PageInfo }>(
    `/admin/orders/history${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      side: opts.side,
      market_id: opts.market_id,
      user_id: opts.user_id,
      currency: opts.currency,
    })}`,
  ).then<Paginated<OrderItem>>((r) => ({ items: r.orders ?? [], page: r.page }));
// Cross-user executed trades.
export const fetchTradeHistory = (
  opts: PageOpts & {
    sort?: SortState;
    side?: string;
    market_id?: string;
    user_id?: string;
    ledger_status?: string;
    // The currency this fill SETTLED in (SM-511) — a stored column on
    // settlement_trades, not a mode in disguise as it is on positions:
    // the cash leg of THIS side, so a GAME buy is RED and a GAME sell
    // is GREEN. All three codes are reachable here.
    currency?: string;
  },
) =>
  api<{ trades: SettlementTradeItem[]; page: PageInfo }>(
    `/admin/trades${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      side: opts.side,
      market_id: opts.market_id,
      user_id: opts.user_id,
      ledger_status: opts.ledger_status,
      currency: opts.currency,
    })}`,
  ).then<Paginated<SettlementTradeItem>>((r) => ({
    items: r.trades ?? [],
    page: r.page,
  }));
export const fetchTickers = () =>
  api<{ tickers: TickerSnapshot[] }>("/admin/markets/tickers").then((r) => r.tickers ?? []);
export const fetchTrades = (instrumentID: string, limit = 30) =>
  api<{ trades: TradePrint[] }>(`/admin/markets/${instrumentID}/trades${qs({ limit })}`)
    .then((r) => r.trades ?? []);

export const fetchEngineStatus = () =>
  api<EngineStatus>("/admin/engine/status");
export const fetchPeers = () =>
  api<{ peers: PeerInfo[] }>("/admin/engine/peers").then((r) => r.peers ?? []);
export const fetchOwnership = () =>
  api<{ leases: OwnershipLease[] }>("/admin/engine/ownership").then((r) => r.leases ?? []);

export const fetchConfigs = (opts: PageOpts & { sort?: SortState }) =>
  api<{ configs: SystemConfigItem[]; page: PageInfo }>(
    `/admin/system/configs${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
    })}`,
  ).then<Paginated<SystemConfigItem>>((r) => ({
    items: r.configs ?? [],
    page: r.page,
  }));

// --- mutations ---

export const enrollMFA = (kind?: string, friendlyName?: string) =>
  api<EnrollMFAResult>("/admin/me/mfa/factors", {
    method: "POST",
    body: { kind: kind ?? "TOTP", friendly_name: friendlyName ?? "" },
  });
export const verifyMFA = (factorID: string, code: string) =>
  api<{ ok: boolean }>(`/admin/me/mfa/factors/${factorID}/verify`, {
    method: "POST",
    body: { code },
  });
export const revokeMFA = (factorID: string) =>
  api<{ ok: boolean }>(`/admin/me/mfa/factors/${factorID}`, { method: "DELETE" });

export interface CreateAdminInput {
  email: string;
  password: string;
  display_name?: string;
  roles?: string[];
}
export const createAdmin = (input: CreateAdminInput) =>
  api<AdminEntity>("/admin/admins", { method: "POST", body: input });

// --- passwords (SM-534) ---
//
// Two endpoints, one server-side chokepoint. Neither response ever
// echoes a password the caller sent; the operator-set one returns a
// GENERATED temporary password once and only once.

export const changeMyPassword = (input: ChangeMyPasswordInput) =>
  api<ChangePasswordResult>("/admin/me/password", { method: "POST", body: input });

/**
 * Ask the server to mint a temporary password for `id`. The operator
 * hands it over out of band; the admin is forced to replace it at their
 * next sign-in, and every session they had is ended.
 *
 * `actorPassword` is the CALLER's own password, not the target's — the
 * recent-authentication step-up (SM-535). The server checks it before
 * anything is written, and refuses identically whether or not the
 * target exists.
 *
 * Deliberately takes no NEW password argument. Letting an operator type
 * one is supported by the API but is the worse path — a typed password
 * gets reused, and the response would have to reflect a secret the
 * caller sent back at them.
 */
export const setAdminPassword = (id: string, actorPassword: string) =>
  api<SetAdminPasswordResult>(`/admin/admins/${id}/password`, {
    method: "POST",
    body: { actor_password: actorPassword },
  });

export const setAdminStatus = (id: string, status: string) =>
  api(`/admin/admins/${id}/status`, { method: "POST", body: { status } });
export const assignAdminRole = (id: string, role: string) =>
  api(`/admin/admins/${id}/roles`, { method: "POST", body: { role } });
export const revokeAdminRole = (id: string, role: string) =>
  api(`/admin/admins/${id}/roles/${role}`, { method: "DELETE" });

export interface CreateCategoryInput {
  name: string;
  description?: string;
  parent_id?: string;
  icon_url?: string;
  display_order?: number;
}
export interface UpdateCategoryInput {
  name?: string;
  description?: string;
  parent_id?: string;
  icon_url?: string;
  display_order?: number;
  is_archived?: boolean;
  is_published?: boolean;
}
export const fetchCategory = (id: string) =>
  api<CategoryItem>(`/admin/catalog/categories/${id}`);
export const createCategory = (input: CreateCategoryInput) =>
  api<CategoryItem>("/admin/catalog/categories", { method: "POST", body: input });
export const updateCategory = (id: string, input: UpdateCategoryInput) =>
  api<CategoryItem>(`/admin/catalog/categories/${id}`, { method: "PATCH", body: input });
export const setCategoryPublished = (id: string, published: boolean) =>
  api<CategoryItem>(`/admin/catalog/categories/${id}/publish`, {
    method: "POST", body: { published },
  });
export const deleteCategory = (id: string) =>
  api(`/admin/catalog/categories/${id}`, { method: "DELETE" });

export interface CreateTopicInput {
  category_id: string;
  name: string;
  description?: string;
  rules?: string;
  market_context?: string;
  icon_url?: string;
  display_order?: number;
  badge_mode?: "crown" | "image" | "none";
  outcome_order_mode?: "dynamic" | "fixed";
}
export interface UpdateTopicInput {
  category_id?: string;
  name?: string;
  description?: string;
  rules?: string;
  market_context?: string;
  icon_url?: string;
  display_order?: number;
  is_archived?: boolean;
  is_published?: boolean;
  badge_mode?: "crown" | "image" | "none";
  outcome_order_mode?: "dynamic" | "fixed";
  // SM-425 per-event trading-mode switches (SM-446 renders them).
  // Turning one off here takes the mode away from EVERY market under
  // the event — market-service fans the change to each child whose own
  // flag is set. Omitted = unchanged.
  cash_enabled?: boolean;
  game_enabled?: boolean;
}
export const fetchTopic = (id: string) =>
  api<EventItem>(`/admin/catalog/topics/${id}`);
export const createTopic = (input: CreateTopicInput) =>
  api<EventItem>("/admin/catalog/topics", { method: "POST", body: input });
export const updateTopic = (id: string, input: UpdateTopicInput) =>
  api<EventItem>(`/admin/catalog/topics/${id}`, { method: "PATCH", body: input });
// setTopicPublished flips one event's publish state.
//
// A publish that would leave the event live but unreachable — no
// published market under it, or no trading mode that reaches one — is
// REFUSED with MARKET_000305 and a sentence naming the fix (SM-520).
// Pass allowHidden once the operator has seen that sentence and asked
// for it anyway; the response then carries the "published, not visible"
// verdict so the panel can say so immediately.
export const setTopicPublished = (
  id: string,
  published: boolean,
  allowHidden = false,
) =>
  api<EventItem>(`/admin/catalog/topics/${id}/publish`, {
    method: "POST",
    body: allowHidden ? { published, allow_hidden: true } : { published },
  });

// EVENT_NOT_VISIBLE_CODE is market-service's refusal for the publish
// above. Exported so the panels can tell "you must confirm this" apart
// from a real failure without string-matching the message.
export const EVENT_NOT_VISIBLE_CODE = "MARKET_000305";
// Hard delete — the server refuses while the event still has child
// markets (MARKET_000302); archiving is updateTopic({ is_archived }).
export const deleteTopic = (id: string) =>
  api(`/admin/catalog/topics/${id}`, { method: "DELETE" });

// --- comments (per-market-group discussion moderation) ---
//
// fetchComments lists every comment on a market group (any status,
// full bodies), newest first; deleteComment takes one down (the server
// flips it to HIDDEN). groupID flows through to the audit row so the
// log records which event the removed comment lived on.
export const fetchComments = (groupID: string, opts: PageOpts) =>
  api<{ comments: AdminComment[]; page: PageInfo }>(
    `/admin/comments${qs({
      market_group_id: groupID,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  ).then<Paginated<AdminComment>>((r) => ({ items: r.comments ?? [], page: r.page }));
export const deleteComment = (commentID: string, groupID: string) =>
  api(`/admin/comments/${commentID}${qs({ market_group_id: groupID })}`, {
    method: "DELETE",
  });

// --- icon endpoints (catalog) ---
//
// Three flavors per owner kind, mirroring backoffice-service's six new
// /admin/catalog/{categories|topics}/{id}/icon* routes:
//
//   * upload* — multipart POST with a `file` field. backoffice
//     validates the image (MIME + size + decode), writes to GCS, and
//     PATCH-es market-service icon_url with the resulting URL.
//
//   * setUrl* — JSON POST with {url}. backoffice validates the URL
//     (https + public host) and PATCH-es market-service. No bytes
//     move through us — the browser fetches the URL directly.
//
//   * remove* — DELETE; clears icon_url. The GCS object (if any)
//     is left in place (v1; orphan cleanup is deferred).
export interface MediaUploadResponse {
  url: string;
  key: string;
  content_type: string;
  size_bytes: number;
}

/** The one-field multipart body every image upload in this console sends —
 *  the "file" field backoffice-service reads. Exported so an upload that lives
 *  in another query module (currency artwork) sends the same shape rather than
 *  building a second one. */
export const iconFormData = (file: File): FormData => {
  const fd = new FormData();
  fd.append("file", file);
  return fd;
};

export const uploadCategoryIcon = (id: string, file: File) =>
  api<MediaUploadResponse>(`/admin/catalog/categories/${id}/icon`, {
    method: "POST", body: iconFormData(file),
  });
export const setCategoryIconURL = (id: string, url: string) =>
  api<{ url: string }>(`/admin/catalog/categories/${id}/icon/url`, {
    method: "POST", body: { url },
  });
export const removeCategoryIcon = (id: string) =>
  api(`/admin/catalog/categories/${id}/icon`, { method: "DELETE" });

export const uploadTopicIcon = (id: string, file: File) =>
  api<MediaUploadResponse>(`/admin/catalog/topics/${id}/icon`, {
    method: "POST", body: iconFormData(file),
  });
export const setTopicIconURL = (id: string, url: string) =>
  api<{ url: string }>(`/admin/catalog/topics/${id}/icon/url`, {
    method: "POST", body: { url },
  });
export const removeTopicIcon = (id: string) =>
  api(`/admin/catalog/topics/${id}/icon`, { method: "DELETE" });

// Market icons + outcome icons follow the same three-flavor pattern.
// Backoffice writes to GCS and PATCH-es the icon_url on market-service.
export const uploadMarketIcon = (id: string, file: File) =>
  api<MediaUploadResponse>(`/admin/catalog/rules/${id}/icon`, {
    method: "POST", body: iconFormData(file),
  });
export const setMarketIconURL = (id: string, url: string) =>
  api<{ url: string }>(`/admin/catalog/rules/${id}/icon/url`, {
    method: "POST", body: { url },
  });
export const removeMarketIcon = (id: string) =>
  api(`/admin/catalog/rules/${id}/icon`, { method: "DELETE" });

export const uploadOutcomeIcon = (id: string, file: File) =>
  api<MediaUploadResponse>(`/admin/catalog/outcomes/${id}/icon`, {
    method: "POST", body: iconFormData(file),
  });
export const setOutcomeIconURL = (id: string, url: string) =>
  api<{ url: string }>(`/admin/catalog/outcomes/${id}/icon/url`, {
    method: "POST", body: { url },
  });
export const removeOutcomeIcon = (id: string) =>
  api(`/admin/catalog/outcomes/${id}/icon`, { method: "DELETE" });

// Admin profile picture. Uploads to GCS via backoffice-service, which
// persists avatar_url straight to the admins table (no set-by-URL
// flavor). Same multipart shape as the icon uploads (field "file").
export const uploadAdminAvatar = (id: string, file: File) =>
  api<MediaUploadResponse>(`/admin/admins/${id}/avatar`, {
    method: "POST", body: iconFormData(file),
  });
export const removeAdminAvatar = (id: string) =>
  api(`/admin/admins/${id}/avatar`, { method: "DELETE" });

// --- Campaigns (promotion-service via backoffice /admin/campaigns) ---
//
// Monetary amounts are public 4dp minor (reward_amount_minor /
// total_budget_minor): the form enters SPARK and converts with
// parseSparkToMinor; display uses SparkAmount/formatSpark. Weekdays are
// 0=Sun..6=Sat. Times are RFC3339; time_of_day is "HH:MM".
//
// SM-615: delivery is the CREDIT TYPE — AUTO (backend fan-out) or VISIT
// (authenticated check-in). CLAIM is what a pre-SM-615 promotion-service
// still returns for the same thing; new campaigns never send it. Timing is
// schedule_kind for BOTH credit types (see lib/campaigns.ts).
export type CampaignDelivery = "AUTO" | "VISIT" | "CLAIM";
export type CampaignStatus = "DRAFT" | "ACTIVE" | "DISABLED";
export type CampaignButtonPosition = "TOP" | "BOTTOM" | "LEFT" | "RIGHT";
export type CampaignAudience = "ALL" | "ACTIVE_SINCE";
export type CampaignScheduleKind = "ONCE" | "DAILY" | "WEEKLY";

export interface Campaign {
  campaign_id: string;
  name: string;
  description: string;
  status: CampaignStatus;
  delivery: CampaignDelivery;
  reward_amount_minor: number;
  // SM-436: the campaign's single reward currency (RED|GREEN|GOLD).
  // Absent on rows from a pre-0003 promotion-service = legacy GOLD.
  // Immutable once the campaign leaves DRAFT.
  currency?: string;
  // SM-436: cap on the recipient's RESULTING balance (public 4dp
  // minor, same scale as reward). Absent/null = uncapped.
  max_balance_minor?: number | null;
  audience: CampaignAudience;
  audience_days?: number;
  per_user_limit: number;
  total_budget_minor?: number;
  credited_total_minor: number;
  starts_at: string;
  ends_at?: string;
  // SM-152: the ledger system account transfer-mode claims draw from.
  // Absent/empty = the promotion-service config default (surface it via
  // fetchCampaignFundingOptions().default_account_code).
  funding_account_code?: string;
  // VISIT success popup: image + heading shown AFTER the credit (SM-615).
  image_url: string;
  popup_title?: string;
  // Legacy CLAIM dialog fields — readable for compatibility, never set by
  // the form any more.
  button_text: string;
  button_position: CampaignButtonPosition;
  // Timing — set for both credit types; absent on a pre-0004 row = Once.
  schedule_kind?: CampaignScheduleKind;
  run_at?: string;
  time_of_day?: string;
  weekdays?: number[];
  timezone: string;
  next_run_at?: string;
  last_run_at?: string;
  created_by_admin_id: string;
  created_at: string;
  updated_at: string;
}

export interface CampaignRecipient {
  user_id: string;
  // Resolved server-side from client-service (backoffice composer). Best-effort:
  // absent when the lookup is unavailable, so render falls back to user_id.
  email?: string;
  display_name?: string;
  total_minor: number;
  claim_count: number;
  last_claim_at: string;
}

export interface CampaignBlacklistEntry {
  campaign_id: string;
  user_id: string;
  reason: string;
  created_by_admin_id: string;
  created_at: string;
}

export interface CampaignCreateInput {
  name: string;
  description?: string;
  status?: CampaignStatus;
  delivery: CampaignDelivery;
  reward_amount_minor: number;
  // SM-436: omitted = GOLD. Server rejects unknown codes and non-GOLD
  // creation while FF_PROMO_CURRENCY is off.
  currency?: string;
  // SM-436: 0 clears the cap (uncapped) on update; > 0 sets it.
  max_balance_minor?: number;
  audience?: CampaignAudience;
  audience_days?: number;
  per_user_limit?: number;
  // SM-737: campaign-wide spend cap, public 4dp minor. Omitted on create =
  // unlimited (NULL); on update 0 CLEARS an existing budget back to
  // unlimited (JSON null and "absent" are the same nil pointer server-side).
  total_budget_minor?: number;
  starts_at: string;
  ends_at?: string;
  // SM-152: "" = fall back to the platform default funding account.
  // Only emitted when the funding section rendered (transfer mode live).
  funding_account_code?: string;
  image_url?: string;
  popup_title?: string;
  // Legacy fields — accepted by the server, not emitted by the form.
  button_text?: string;
  button_position?: CampaignButtonPosition;
  // ONCE | DAILY | WEEKLY. A ONCE occurrence starts at starts_at (no run_at
  // is sent; the server defaults it).
  schedule_kind?: CampaignScheduleKind;
  run_at?: string;
  time_of_day?: string;
  weekdays?: number[];
  timezone?: string;
}
export type CampaignUpdateInput = Partial<CampaignCreateInput>;

export const fetchCampaigns = (
  opts: PageOpts & {
    q?: string;
    status?: string;
    delivery?: string;
    // Effective funding-account match: explicit overrides plus, when the
    // code is the platform default, campaigns with no override.
    funding_account_code?: string;
    sort?: SortState;
  },
) =>
  api<{ campaigns: Campaign[]; total: number }>(
    `/admin/campaigns${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: opts.q,
      status: opts.status,
      delivery: opts.delivery,
      funding_account_code: opts.funding_account_code,
      ...sortQS(opts.sort),
    })}`,
  ).then((r) => ({ items: r.campaigns ?? [], total: r.total ?? 0 }));

export const fetchCampaign = (id: string) => api<Campaign>(`/admin/campaigns/${id}`);

export const createCampaign = (input: CampaignCreateInput) =>
  api<Campaign>("/admin/campaigns", { method: "POST", body: input });

export const updateCampaign = (id: string, input: CampaignUpdateInput) =>
  api<Campaign>(`/admin/campaigns/${id}`, { method: "PATCH", body: input });

export const setCampaignStatus = (id: string, status: CampaignStatus) =>
  api<Campaign>(`/admin/campaigns/${id}/status`, { method: "POST", body: { status } });

export const deleteCampaign = (id: string) =>
  api(`/admin/campaigns/${id}`, { method: "DELETE" });

// Campaign image: upload to GCS via backoffice (no campaign id — the create
// form uploads first), returns the URL the create/update payload stores.
export const uploadCampaignImage = (file: File) =>
  api<MediaUploadResponse>("/admin/campaigns/image", {
    method: "POST",
    body: iconFormData(file),
  });

export const fetchCampaignRecipients = (id: string, opts: PageOpts) =>
  api<{ recipients: CampaignRecipient[]; total: number }>(
    `/admin/campaigns/${id}/recipients${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({ items: r.recipients ?? [], total: r.total ?? 0 }));

// --- SM-152: campaign funding options ---
//
// Composite the backoffice builds from promotion-service (is
// transfer-funding live? which account is the default?) and the
// ledger's SYSTEM_PROMOTION subtree (the accounts a campaign may draw
// from, with spendable balances). Resolves to null when the feature is
// dark — promotion still in credit mode, or a backoffice build
// predating the endpoint (404) — so funding UI simply doesn't render.
export interface CampaignFundingAccount {
  account_code: string;
  account_name: string;
  available: string; // public 4dp minor string
  // ACTIVE | FROZEN (CLOSED never lists). Absent on backoffice builds
  // predating the field — treat as ACTIVE.
  status?: string;
  // SM-439: the account row's currency — (account_code, currency) is
  // the ledger identity since 0025, so the same code lists once per
  // currency. Absent on backoffice builds predating the field =
  // legacy GOLD. Funding health must only ever compare same-currency.
  currency?: string;
}
export interface CampaignFundingOptions {
  enabled: boolean;
  default_account_code?: string;
  accounts?: CampaignFundingAccount[];
}
export const fetchCampaignFundingOptions = (): Promise<CampaignFundingOptions | null> =>
  api<CampaignFundingOptions>("/admin/campaigns/funding-options")
    .then((r) => (r?.enabled ? r : null))
    .catch(() => null);

// --- SM-440: claim history (requested vs credited, FULL/PARTIAL/SKIPPED) ---

export type CampaignClaimResult = "FULL" | "PARTIAL" | "SKIPPED" | "";
export interface CampaignClaim {
  claim_id: string;
  campaign_id: string;
  campaign_name?: string;
  user_id?: string;
  // SM-738: resolved server-side from client-service by the backoffice
  // composer, the same best-effort batch that names CampaignRecipient.
  // Absent when the lookup is unavailable, so render falls back to user_id.
  display_name?: string;
  email?: string;
  currency: string;
  requested_amount_minor: number;
  credited_amount_minor: number;
  // "" while PENDING/FAILED (pre-terminal); FULL on backfilled legacy
  // CREDITED rows.
  result: CampaignClaimResult;
  reason?: string; // e.g. BALANCE_CAP
  status: string; // PENDING | CREDITED | SKIPPED | FAILED
  source: string; // BUTTON | CLOSE | SCHEDULER
  run_id: string;
  created_at: string;
}

export const fetchCampaignClaims = (
  id: string,
  opts: PageOpts & { result?: string; status?: string; user_id?: string },
) =>
  api<{ claims: CampaignClaim[]; total: number }>(
    `/admin/campaigns/${id}/claims${qs({
      limit: opts.limit,
      offset: opts.offset,
      result: opts.result,
      status: opts.status,
      user_id: opts.user_id,
    })}`,
  ).then((r) => ({ items: r.claims ?? [], total: r.total ?? 0 }));

export const fetchCampaignBlacklist = (id: string) =>
  api<{ blacklist: CampaignBlacklistEntry[]; total: number }>(
    `/admin/campaigns/${id}/blacklist`,
  ).then((r) => r.blacklist ?? []);

export const addCampaignBlacklist = (id: string, user_id: string, reason: string) =>
  api(`/admin/campaigns/${id}/blacklist`, { method: "POST", body: { user_id, reason } });

export const removeCampaignBlacklist = (id: string, userId: string) =>
  api(`/admin/campaigns/${id}/blacklist/${userId}`, { method: "DELETE" });

export interface CreateRuleInput {
  category_id: string;
  event_id?: string;
  // Optional explicit ticker; omitted → the server mints a ULID.
  market_id?: string;
  name: string;
  rules?: string;
  market_context?: string;
  description?: string;
  icon_url?: string;
  tick_size?: string;
  lot_size?: string;
  min_amount?: string;
  price_precision?: number;
  amount_precision?: number;
  // No fee fields: a new market's rate is its effective fee policy
  // (SM-947); market-service ignores any rate sent here.
  stp_mode?: string;
  outcomes: { code: string; name: string; icon_url?: string }[];
}

// Prices are fractional dollars on the $0–$1 scale (SM-460). At the
// standard price_precision of 4, wire tick_size 100 renders as $0.01
// (the retired SPARK-cents lens showed the same wire value as ⚡1.00).
export const DEFAULT_MARKET_TICK_SIZE = "100";

// idempotencyKey is optional but every interactive caller should pass
// one: it is what stops a duplicate delivery of the same create (a
// double-click that beats the disabled re-render, a browser resend, a
// proxy retry) from minting a second market. See SM-347 — replaying a
// key returns the market the first attempt produced.
export const createRule = (input: CreateRuleInput, idempotencyKey?: string) =>
  api<MarketItem>("/admin/catalog/rules", {
    method: "POST",
    body: input,
    idempotencyKey,
  });

// Categorical / scalar_thresholds bundle create. Maps 1:1 to
// market-service's POST /admin/events/bundle — the admin form
// authors the parent event + every child market in one submit,
// the server creates them atomically.
export interface CreateEventBundleOption {
  label: string;
  market_id?: string;
  image_url?: string;
  outcome_yes_name?: string;
  outcome_no_name?: string;
}

export interface CreateEventBundleThreshold {
  value: string; // numeric string to preserve precision
  market_id?: string;
}

export interface CreateEventBundleInput {
  category_id: string;
  kind: "categorical" | "scalar_thresholds";
  name: string;
  description?: string;
  // display_meta carries kind-specific shared config (subject /
  // layout for categorical, subject for scalar). Scalar's unit /
  // comparator / precision are top-level below; the server merges
  // them into display_meta on insert.
  display_meta?: Record<string, unknown>;
  rules?: string;
  market_context?: string;
  tick_size?: string;
  lot_size?: string;
  min_amount?: string;
  price_precision?: number;
  amount_precision?: number;
  stp_mode?: string;
  trading_starts_at?: string;
  trading_ends_at?: string;

  // Categorical-specific.
  options?: CreateEventBundleOption[];

  // Scalar_thresholds-specific. Thresholds is the ladder; unit /
  // comparator / numeric_precision are the shared rendering config
  // hoisted onto event.display_meta server-side.
  thresholds?: CreateEventBundleThreshold[];
  unit?: string;
  comparator?: "above" | "below";
  numeric_precision?: number;
}

export interface EventBundleResponse {
  event: EventItem;
  markets: MarketItem[];
}

export const createEventBundle = (input: CreateEventBundleInput) =>
  api<EventBundleResponse>("/admin/catalog/events/bundle", {
    method: "POST",
    body: input,
  });

// Event-level resolve. Body shape depends on event kind:
//
//   categorical: winning_market_id (or voided=true)
//   scalar_thresholds: settled_value (or voided=true)
//
// Excess fields are ignored server-side so the same shape works for
// both flows.
export interface ResolveEventInput {
  winning_market_id?: string;
  settled_value?: string;
  voided?: boolean;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface EventResolveResponse {
  event: EventItem;
  resolutions: {
    resolution_id: string;
    market_id: string;
    winning_outcome_id?: string;
    kind: string;
    resolved_at: string;
  }[];
}

export const resolveEvent = (eventID: string, input: ResolveEventInput) =>
  api<EventResolveResponse>(`/admin/catalog/events/${eventID}/resolve`, {
    method: "POST",
    body: input,
  });

// Bulk event-level actions. Each fans the action to every eligible
// child; the response carries Updated / Skipped / Failed lists so the
// admin UI can render a partial-success summary.
export interface EventBulkLifecycleInput {
  reason?: string;
}

export interface EventBulkLifecycleResponse {
  event_id: string;
  action: "publish-all" | "unpublish-all" | "cancel";
  total: number;
  // updated/skipped come from Go slices that are nil (→ JSON null) when no
  // child matched the action — e.g. publish-all on an already-published
  // event flips nothing, so updated is null. Treat these as possibly-null
  // and guard before reading .length.
  updated: string[] | null;
  skipped?: string[] | null;
  failed?: {
    market_id: string;
    code?: string;
    message?: string;
  }[];
  /**
   * Per-mode outcome of a publish-all (SM-532), and the one boolean
   * that says the publish landed with a switched-on mode reaching no
   * book. Absent on the other two actions and on a pre-SM-532 server.
   */
  modes?: EventModeStatus[];
  modes_blocked?: boolean;
}

export const publishAllChildren = (eventID: string, input: EventBulkLifecycleInput = {}) =>
  api<EventBulkLifecycleResponse>(
    `/admin/catalog/events/${eventID}/publish-all`,
    { method: "POST", body: input },
  );
export const unpublishAllChildren = (eventID: string, input: EventBulkLifecycleInput = {}) =>
  api<EventBulkLifecycleResponse>(
    `/admin/catalog/events/${eventID}/unpublish-all`,
    { method: "POST", body: input },
  );
/**
 * Push ONE trading mode's switch onto every child market of an event
 * (SM-532).
 *
 * The event switch and each market's own switch are ANDed, and the
 * event page could only ever set half of that AND — so an operator
 * turned GAME on, published, and the client still refused to trade.
 * Per-market writes upstream, so the response is a partial-success
 * summary like the other event fan-outs.
 */
export interface ApplyEventModeInput {
  trading_mode: string;
  enabled: boolean;
}

export interface ApplyEventModeResponse {
  event_id: string;
  trading_mode: string;
  enabled: boolean;
  total: number;
  updated: string[];
  skipped?: string[] | null;
  failed?: {
    market_id: string;
    code?: string;
    message?: string;
  }[];
}

export const applyEventMode = (eventID: string, input: ApplyEventModeInput) =>
  api<ApplyEventModeResponse>(
    `/admin/catalog/events/${eventID}/apply-mode`,
    { method: "POST", body: input },
  );

export const cancelEvent = (eventID: string, input: EventBulkLifecycleInput) =>
  api<EventBulkLifecycleResponse>(
    `/admin/catalog/events/${eventID}/cancel`,
    { method: "POST", body: input },
  );

// updateRule patches non-status, non-fee fields on an existing market.
// Only the supplied keys are sent — undefined fields are stripped server
// side. Status transitions (open/close/cancel/resolve/settle) and fee
// changes go through their dedicated endpoints.
export interface UpdateRuleInput {
  name?: string;
  rules?: string;
  market_context?: string;
  description?: string;
  icon_url?: string;
  tick_size?: string;
  lot_size?: string;
  min_amount?: string;
  price_precision?: number;
  amount_precision?: number;
  stp_mode?: string;
  trading_starts_at?: string;
  trading_ends_at?: string;
  // Sibling order within the parent event. Set by the drag-to-order UI
  // when the event is in "fixed" outcome-order mode.
  display_order?: number;
  // SM-425 per-market trading-mode switches (SM-446 renders them).
  // Omitted = unchanged; the server treats a nil pointer as "leave
  // alone", so sending only the flag that moved is both correct and the
  // smallest possible audit row.
  cash_enabled?: boolean;
  game_enabled?: boolean;
}
export const updateRule = (id: string, input: UpdateRuleInput) =>
  api<MarketItem>(`/admin/catalog/rules/${id}`, { method: "PATCH", body: input });

// Post-migration-0014 the market lifecycle collapses to three terminal
// shapes plus an independent published toggle. open/close/settle no
// longer exist on the server; publish/unpublish toggles engine exposure
// and resolve folds the prior close+resolve+settle into one click.
export const setMarketPublished = (id: string, published: boolean) =>
  api<MarketItem>(`/admin/catalog/rules/${id}/publish`, {
    method: "POST", body: { published },
  });
export const cancelRule = (id: string, reason?: string) =>
  api<MarketItem>(`/admin/catalog/rules/${id}/cancel`, {
    method: "POST", body: reason ? { reason } : {},
  });
export const resolveRule = (id: string, winning_outcome_id: string, notes?: string) =>
  api<MarketItem>(`/admin/catalog/rules/${id}/resolve`, {
    method: "POST",
    body: notes ? { winning_outcome_id, notes } : { winning_outcome_id },
  });
export const deleteMarket = (id: string) =>
  api(`/admin/catalog/rules/${id}`, { method: "DELETE" });
export const setRuleSTP = (id: string, stp_mode: string) =>
  api(`/admin/catalog/rules/${id}/stp`, { method: "POST", body: { stp_mode } });

// Per-outcome edit: code / name / display_order / icon_url. Server
// refuses edits on terminal markets.
export interface UpdateOutcomeInput {
  code?: string;
  name?: string;
  display_order?: number;
  icon_url?: string;
}
export const updateOutcome = (outcomeID: string, input: UpdateOutcomeInput) =>
  api<MarketOutcome>(`/admin/catalog/outcomes/${outcomeID}`, {
    method: "PATCH", body: input,
  });

// --- languages + catalog translations (SM-283 multi-language catalog) ---
//
// The language registry is dynamic (market-service `languages` table),
// proxied by backoffice under /admin/catalog/languages. Per-entity
// translations live at
//   /admin/catalog/{categories|topics|rules|outcomes}/{id}/translations
// — same topics=events / rules=markets path mapping as the icon
// endpoints above. The GET map always contains an `en` entry
// synthesized from the base (canonical English) columns plus one entry
// per locale that has a stored row. PUT bodies use pointer semantics:
// an omitted field is unchanged, "" is an explicit clear (public reads
// then fall back to the English value for that field). Writing
// locale=en maps onto the base entity through the normal update path
// server-side — the TranslationsEditor keeps en read-only instead.
export interface CreateLanguageInput {
  code: string;
  name: string;
  native_name?: string;
  display_order?: number;
}
// PATCH body. `code` is the NEW code — a locale-code rename (SM-314).
// The path segment still carries the CURRENT code; sending `code` asks
// market-service to migrate every *_translations row from the old
// locale to the new one and move the registry row's primary key, all in
// one transaction. Omit it for a plain field edit. Renaming `en` is
// refused server-side (MARKET_000704), as is a code already registered
// (MARKET_000702).
export interface UpdateLanguageInput {
  code?: string;
  name?: string;
  native_name?: string;
  enabled?: boolean;
  display_order?: number;
}
export const listLanguages = () =>
  api<{ languages: LanguageItem[] }>("/admin/catalog/languages").then(
    (r) => r.languages ?? [],
  );
export const createLanguage = (input: CreateLanguageInput) =>
  api<LanguageItem>("/admin/catalog/languages", { method: "POST", body: input });
export const updateLanguage = (code: string, input: UpdateLanguageInput) =>
  api<LanguageItem>(`/admin/catalog/languages/${encodeURIComponent(code)}`, {
    method: "PATCH", body: input,
  });

// --- public event ordering (SM-355) ---
//
// One GLOBAL config proxied at /admin/catalog/event-ordering;
// market-service applies it server-side as the trader app's default
// event sort. PUT bodies may be partial (criteria the admin ranked);
// the server normalizes to the full field ladder and echoes the stored
// row. The preview runs a DRAFT criteria set against the live public
// catalog without saving — criteria ride the query string as a
// `field:direction` CSV.
export interface PutEventOrderingInput {
  mode: EventOrderMode;
  pinned_first: boolean;
  criteria: EventOrderCriterion[];
  // Sent in both modes: the server parks the list while mode is auto.
  manual_event_ids: string[];
}
export const getEventOrdering = () =>
  api<EventOrderingConfig>("/admin/catalog/event-ordering");
export const putEventOrdering = (input: PutEventOrderingInput) =>
  api<EventOrderingConfig>("/admin/catalog/event-ordering", {
    method: "PUT", body: input,
  });
export const criteriaCSV = (criteria: EventOrderCriterion[]): string =>
  criteria.map((c) => `${c.field}:${c.direction}`).join(",");
// The AUTO-mode preview: what the rules would produce. Manual mode has
// no preview call — the list the operator arranges is already the feed
// order, so previewing it would re-render the same rows and pay a
// round-trip to be told what they just dragged.
export const previewEventOrdering = (
  criteria: EventOrderCriterion[],
  pinnedFirst: boolean,
  limit: number,
) =>
  api<{ events: EventItem[]; page: PageInfo }>(
    `/admin/catalog/event-ordering/preview${qs({
      criteria: criteriaCSV(criteria),
      pinned_first: String(pinnedFirst),
      limit,
    })}`,
  );
// The published catalog behind manual mode, in the order the RULES
// currently rank it. It is the list the operator arranges — and the
// "reset to rule order" source — so it is a plain ordered read, not a
// search. Unscoped upstream, because the order it feeds is global.
export const eventOrderingCatalog = (limit: number) =>
  api<{ events: EventItem[]; page: PageInfo }>(
    `/admin/catalog/event-ordering/catalog${qs({ limit })}`,
  );

export type TranslationEntityKind =
  | "categories"
  | "topics"
  | "rules"
  | "outcomes";
// field key → localized text, e.g. { name: "...", rules: "..." }. Which
// keys are valid depends on the entity kind (the server rejects unknown
// fields with MARKET_000705); callers pass the per-kind field list.
export type TranslationFields = Record<string, string>;
export const getTranslations = (kind: TranslationEntityKind, id: string) =>
  api<{ translations: Record<string, TranslationFields> }>(
    `/admin/catalog/${kind}/${id}/translations`,
  ).then((r) => r.translations ?? {});
export const putTranslation = (
  kind: TranslationEntityKind,
  id: string,
  locale: string,
  fields: TranslationFields,
) =>
  api<TranslationFields>(
    `/admin/catalog/${kind}/${id}/translations/${encodeURIComponent(locale)}`,
    { method: "PUT", body: fields },
  );
export const deleteTranslation = (
  kind: TranslationEntityKind,
  id: string,
  locale: string,
) =>
  api(
    `/admin/catalog/${kind}/${id}/translations/${encodeURIComponent(locale)}`,
    { method: "DELETE" },
  );

// A market's own fee rate is a MARKET-scope fee policy (createFeeProfile
// below). The old per-market write, POST /admin/catalog/rules/{id}/fees,
// is gone (SM-947): it wrote the market row only, so the catalog showed
// a rate that order-service — which charges the policy chain — never
// applied.

// --- Phase 2: fee_profiles CRUD + resolution + audit ---
//
// Resolution chain: MARKET override > CATEGORY default > GLOBAL default
// > zero. Service-layer write path archives the predecessor and inserts
// a successor with bumped version. See market-service/internal/service/
// fees.go for the determinism contract.

export type FeeScope = "GLOBAL" | "CATEGORY" | "MARKET";

/**
 * The currency a fee policy prices (SM-510). A policy applies to ONE
 * currency; the resolution chain is walked within it, and a GOLD policy
 * never changes a RED or GREEN fee.
 *
 * The empty string is the LEGACY marker, not a fourth currency: every
 * row written before SM-510 carried one shared rate that priced every
 * book, and migration 0036 keeps them verbatim so no market's effective
 * fee moved on deploy. The API refuses to WRITE one, so the set only
 * shrinks — resolution still reads it as a level's own fallback until
 * that level's currency policies exist.
 */
export type FeePolicyCurrency = CurrencyCode | "";

export interface FeeProfile {
  profile_id: string;
  scope: FeeScope;
  scope_id: string;        // "_global" for GLOBAL; category_id; market_id
  currency: FeePolicyCurrency;
  maker_fee_rate: string;  // int64 minor units as string
  taker_fee_rate: string;
  fee_rate_precision: number;
  // The redemption fee this profile configures (SM-332), or null when
  // it configures none and markets under it inherit from the next
  // level up. null and "0" mean different things: "0" is a deliberate
  // "charge nothing here" that STOPS the resolution walk.
  //
  // Rows written before SM-332 arrive as null, which is the truth —
  // they configured no redemption fee.
  redemption_fee_rate: string | null;
  status: "ACTIVE" | "ARCHIVED" | "SCHEDULED";
  effective_at: string;
  version: string;
  // Monotonic row identity across the whole table, distinct from the
  // per-scope `version` label. It is what a redemption snapshot
  // references, so it is the number to quote in a support answer about
  // which rate priced a payout.
  revision: number;
  note?: string;
  created_by?: string;
  created_at: string;
}

export interface EffectiveFee {
  market_id: string;
  // The currency these rates are collected in — the one that was asked
  // for. Nothing here is converted from another (SM-510).
  currency: CurrencyCode;
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision: number;
  source: FeeScope | "ZERO";
  source_profile_id?: string;
  version?: string;
  // The redemption fee resolves on its own chain, so it reports its own
  // source: a market override that only sets maker/taker leaves this
  // pointing at the category or the platform.
  redemption_fee_rate: string;
  redemption_source: FeeScope | "ZERO";
  redemption_source_profile_id?: string;
  redemption_version?: string;
  redemption_revision: number;
}

export interface FeeAudit {
  audit_id: number;
  profile_id: string;
  scope: FeeScope;
  scope_id: string;
  currency: FeePolicyCurrency;
  action: "CREATE" | "UPDATE" | "ARCHIVE" | "SCHEDULE" | "ACTIVATE";
  before?: FeeProfile;
  after?: FeeProfile;
  actor_admin_id?: string;
  actor_email?: string;
  ip_address?: string;
  note?: string;
  occurred_at: string;
}

/**
 * The `?currency=` filter these endpoints take (SM-510). Three states,
 * and they are three different questions:
 *
 *   undefined / "all" → every currency
 *   GOLD|RED|GREEN    → that currency's policies alone
 *   "legacy"          → the pre-SM-510 shared rows, the ones an
 *                       operator still has to replace
 *
 * "legacy" needs an explicit spelling because the stored value is the
 * empty string, which on a query string is indistinguishable from an
 * absent parameter.
 */
export const LEGACY_FEE_CURRENCY_PARAM = "legacy" as const;
export type FeeCurrencyFilter = CurrencyCode | typeof LEGACY_FEE_CURRENCY_PARAM | "all";

// The currency is part of every fee key. A page cached under a key that
// does not name it would serve RED's policies under GOLD's heading on
// the next render — the exact confusion the filter exists to remove
// (same rule as revenueKeys, SM-495).
export const feeKeys = {
  all: ["admin-fees"] as const,
  profiles: (scope?: FeeScope, currency?: FeeCurrencyFilter) =>
    [...feeKeys.all, "profiles", scope ?? "", currency ?? ""] as const,
  profile: (id: string) => [...feeKeys.all, "profile", id] as const,
  history: (scope: FeeScope, scopeID: string, currency?: FeeCurrencyFilter) =>
    [...feeKeys.all, "history", scope, scopeID, currency ?? ""] as const,
  effective: (marketID: string, currency?: CurrencyCode) =>
    [...feeKeys.all, "effective", marketID, currency ?? ""] as const,
  // Audit rows also exist at USER scope (overrides), which FeeScope
  // (profile scopes) doesn't cover — hence the wider scope union here.
  audit: (
    scope?: FeeScope | "USER",
    scopeID?: string,
    limit?: number,
    offset?: number,
    currency?: FeeCurrencyFilter,
  ) =>
    [...feeKeys.all, "audit", scope ?? "", scopeID ?? "", limit ?? 0, offset ?? 0, currency ?? ""] as const,
};

export const fetchFeeProfiles = (scope?: FeeScope, currency?: FeeCurrencyFilter) =>
  api<{ profiles: FeeProfile[] }>(`/admin/fees/profiles${qs({ scope, currency })}`)
    .then((r) => r.profiles ?? []);

export const fetchFeeProfile = (id: string) =>
  api<FeeProfile>(`/admin/fees/profiles/${id}`);

export interface CreateFeeProfileInput {
  scope: FeeScope;
  scope_id: string;        // ignored for GLOBAL (server normalizes to "_global")
  // REQUIRED (SM-510). The server rejects an omitted or empty one
  // rather than defaulting it: a default would silently recreate the
  // shared rate the ticket removed. At MARKET scope it must be a
  // currency the market's own books can charge.
  currency: CurrencyCode;
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision: number;
  // Omit to leave the redemption fee exactly as the predecessor had it
  // — which is what a form editing only the trading rates must do, or
  // it would drop a redemption override by saying nothing about it.
  // Send a rate to configure one; send clear_redemption_fee_rate to
  // remove it and inherit again.
  //
  // Setting or clearing this needs FEE_CONFIG_UPDATE on top of
  // RULE_UPDATE: it is money routing, not catalog configuration.
  redemption_fee_rate?: string;
  clear_redemption_fee_rate?: boolean;
  note?: string;
  // RFC3339 timestamp. Future → SCHEDULED (no propagation yet,
  // fee-scheduler worker activates at that time). Empty / past →
  // immediate.
  effective_at?: string;
}
export const createFeeProfile = (body: CreateFeeProfileInput) =>
  api<FeeProfile>("/admin/fees/profiles", { method: "POST", body });

export const archiveFeeProfile = (id: string, note?: string) =>
  api<FeeProfile>(`/admin/fees/profiles/${id}/archive`, {
    method: "POST",
    body: note ? { note } : {},
  });

export const fetchFeeProfileHistory = (
  scope: FeeScope,
  scopeID: string,
  limit = 50,
  currency?: FeeCurrencyFilter,
) =>
  api<{ profiles: FeeProfile[] }>(
    `/admin/fees/history${qs({ scope, scope_id: scopeID, limit, currency })}`,
  ).then((r) => r.profiles ?? []);

// An absent currency resolves GOLD server-side — the Cash-book answer
// every caller was getting before SM-510 — so passing one is how a
// caller asks about a Game book.
export const fetchEffectiveFee = (marketID: string, currency?: CurrencyCode) =>
  api<EffectiveFee>(`/admin/fees/effective/${marketID}${qs({ currency })}`);

// --- SM-139/SM-144: user-specific fee overrides ---
//
// A row here beats the whole MARKET > CATEGORY > GLOBAL chain when
// order-service resolves the effective fee for that user's NEWLY
// placed orders (the resolved rate is locked onto each order at
// placement, so removing/changing an override never reprices orders
// already on the book). market_id empty/absent = user-global override
// (all markets); a concrete market_id scopes it to that market and
// beats the user-global row there.

export interface UserFeeOverride {
  override_id: string;
  user_id: string;
  market_id?: string;
  maker_fee_rate: string; // int64 minor units as string
  taker_fee_rate: string;
  fee_rate_precision: number;
  status: "ACTIVE" | "ARCHIVED";
  version: string;
  note?: string;
  created_by?: string;
  created_at: string;
}

export const userFeeKeys = {
  all: ["admin-user-fees"] as const,
  byUser: (userID: string) => [...userFeeKeys.all, "user", userID] as const,
  page: (limit: number, offset: number) =>
    [...userFeeKeys.all, "page", limit, offset] as const,
};

export const fetchUserFeeOverrides = (userID: string) =>
  api<{ overrides: UserFeeOverride[] }>(
    `/admin/fees/user-overrides${qs({ user_id: userID })}`,
  ).then((r) => r.overrides ?? []);

export const fetchAllUserFeeOverrides = (opts: PageOpts) =>
  api<{
    overrides: UserFeeOverride[];
    page: { total: number; limit: number; offset: number };
  }>(`/admin/fees/user-overrides${qs({ limit: opts.limit, offset: opts.offset })}`).then(
    (r) => ({
      items: r.overrides ?? [],
      page: {
        total: r.page?.total ?? 0,
        limit: r.page?.limit ?? opts.limit,
        offset: r.page?.offset ?? opts.offset,
      },
    }),
  );

export interface SetUserFeeOverrideInput {
  user_id: string;
  market_id?: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision: number;
  note?: string;
}
export const setUserFeeOverride = (body: SetUserFeeOverrideInput) =>
  api<UserFeeOverride>("/admin/fees/user-overrides", { method: "POST", body });

export const removeUserFeeOverride = (overrideID: string, note?: string) =>
  api<UserFeeOverride>(`/admin/fees/user-overrides/${overrideID}/remove`, {
    method: "POST",
    body: note ? { note } : {},
  });

// --- Phase 3: platform revenue dashboard ---
//
// /treasury/revenue. Reads from settlement-service which aggregates
// fee_charges with platform_credit_status='COMMITTED'. Only fees that
// actually landed on the platform wallet count.

export interface RevenueSummary {
  // Currency every figure below is denominated in (SM-495) — the one
  // the request asked for, GOLD when it asked for none. Optional
  // because the console can deploy ahead of settlement; the caller
  // falls back to the currency it requested, which is the same answer
  // an older server would have given for GOLD.
  currency?: string;
  total_amount: string;   // int64 minor units as string
  total_fills: number;
  total_maker_amount: string;
  total_taker_amount: string;
  today_amount: string;
  today_fills: number;
  today_maker_amount: string;
  today_taker_amount: string;
  mtd_amount: string;
  mtd_fills: number;
  mtd_maker_amount: string;
  mtd_taker_amount: string;
  // Redemption fees (SM-327). Optional because the frontend can deploy
  // ahead of settlement — render "—" rather than a lying zero when the
  // field is absent, since the maker and taker figures no longer sum to
  // the total on their own.
  total_redemption_amount?: string;
  today_redemption_amount?: string;
  mtd_redemption_amount?: string;
  // Every currency that has collected fee revenue, each with its own
  // arithmetic. Rendered side by side and NEVER added up: three
  // currencies share a wire scale but not a value (G8). Unaffected by
  // the ?currency filter — it is how an operator sees which currencies
  // have revenue at all without asking three times.
  by_currency?: RevenueCurrencyTotals[];
}

/** One currency's slice of the revenue summary. */
export interface RevenueCurrencyTotals {
  currency: string;
  total_amount: string;
  total_fills: number;
  total_maker_amount: string;
  total_taker_amount: string;
  today_amount: string;
  today_fills: number;
  mtd_amount: string;
  mtd_fills: number;
  total_redemption_amount: string;
}

export interface RevenueDailyRow {
  // Currency this row's amounts are in — see RevenueSummary.currency.
  currency?: string;
  day: string;            // YYYY-MM-DD (UTC)
  amount: string;
  fills: number;
  // Redemption fees (SM-327) — see RevenueSummary.
  redemption_amount?: string;
  redemption_fills?: number;
  maker_amount: string;
  maker_fills: number;
  taker_amount: string;
  taker_fills: number;
}

// The currency is part of BOTH keys (SM-495). A revenue figure is
// meaningless without its currency, so caching one under a key that does
// not name it would serve RED's total under GOLD's label on the next
// render — the exact confusion the filter exists to remove.
export const revenueKeys = {
  all: ["admin-revenue"] as const,
  summary: (currency?: string) => [...revenueKeys.all, "summary", currency ?? ""] as const,
  daily: (days: number, currency?: string) =>
    [...revenueKeys.all, "daily", days, currency ?? ""] as const,
};

export const fetchRevenueSummary = (currency?: string) =>
  api<RevenueSummary>(`/admin/revenue/summary${qs({ currency })}`);

export const fetchRevenueDaily = (days = 30, currency?: string) =>
  api<{ days: RevenueDailyRow[] }>(`/admin/revenue/daily${qs({ days, currency })}`)
    .then((r) => r.days ?? []);

export const fetchFeeAudit = (
  opts: PageOpts & {
    scope?: FeeScope | "USER";
    scope_id?: string;
    // Narrows the SERVER'S page (SM-510). Filtering the returned rows
    // instead would make the total lie and hide changes that live on a
    // later page.
    currency?: FeeCurrencyFilter;
  },
) =>
  api<{ audits: FeeAudit[]; page: { total: number; limit: number; offset: number } }>(
    `/admin/fees/audit${qs({
      limit: opts.limit,
      offset: opts.offset,
      scope: opts.scope,
      scope_id: opts.scope_id,
      currency: opts.currency,
    })}`,
  ).then((r) => ({
    items: r.audits ?? [],
    page: { total: r.page?.total ?? 0, limit: r.page?.limit ?? opts.limit, offset: r.page?.offset ?? opts.offset },
  }));

// --- Phase 4: scheduled profiles ---

export const fetchScheduledFeeProfiles = () =>
  api<{ profiles: FeeProfile[] }>("/admin/fees/scheduled")
    .then((r) => r.profiles ?? []);

export const cancelScheduledFeeProfile = (profileID: string, note?: string) =>
  api<FeeProfile>(`/admin/fees/profiles/${profileID}/cancel-scheduled`, {
    method: "POST",
    body: note ? { note } : {},
  });

// Extend the existing createFeeProfile to accept effective_at. Backend
// treats empty / past as "activate immediately", future as
// status=SCHEDULED.
//
// We keep the previous CreateFeeProfileInput shape and add the
// optional field — callers that don't supply effective_at preserve
// the immediate-activation behavior from Phases 1-3.

export const freezeWallet = (userID: string, reason = "operator action") =>
  api(`/admin/wallets/${userID}/freeze`, { method: "POST", body: { reason } });
export const unfreezeWallet = (userID: string, reason = "operator action") =>
  api(`/admin/wallets/${userID}/unfreeze`, { method: "POST", body: { reason } });
/**
 * A manual operator credit ("deposit") against one user wallet.
 *
 * `currency` and `idempotencyReference` are both additive (SM-472).
 * Omitting the currency reproduces the pre-epic call exactly — the
 * server reads absent as GOLD and sends the same legacy wire code — so
 * an old call site cannot change meaning by staying still.
 *
 * The reference is what the server turns into the deposit's
 * external_reference, and the ledger's per-reference dedup is what makes
 * a retried submit refuse instead of double-crediting. Mint it once per
 * operator intent, never per attempt.
 */
export const creditWallet = (
  userID: string,
  amount: string,
  reason: string,
  currency?: string,
  idempotencyReference?: string,
) =>
  api(`/admin/wallets/${userID}/credit`, {
    method: "POST",
    body: { amount, reason, currency, idempotency_key: idempotencyReference },
  });
// debitWallet is the mirror of creditWallet — a manual admin debit
// (negative ADJUSTMENT) against available balance. The server rejects an
// over-debit (insufficient credit) or a frozen wallet; the error surfaces
// inline on the adjustment panel.
export const debitWallet = (
  userID: string,
  amount: string,
  reason: string,
  currency?: string,
  idempotencyReference?: string,
) =>
  api(`/admin/wallets/${userID}/debit`, {
    method: "POST",
    body: { amount, reason, currency, idempotency_key: idempotencyReference },
  });
// transferWallet moves balance from one user's wallet to another's in
// ONE atomic ledger transaction (a rejected transfer touches neither
// balance). The server rejects a frozen or under-funded source and a
// closed destination with the same coded errors the adjustment panel
// gets; the error surfaces inline on the transfer page. operationID is
// the per-confirm idempotency key: a retried submit with the same key
// replays the original transfer server-side instead of double-moving.
//
// `currency` names the SINGLE currency both legs move in (SM-492). It is
// additive and optional for the same reason the credit/debit pair's is:
// absent reads as GOLD all the way down, so a caller that has not been
// updated keeps the exact behaviour it had. A transfer is always
// same-currency — a cross-currency move is a conversion at a rate, which
// this endpoint has no rate for and must never be asked to perform.
export const transferWallet = (
  fromUserID: string,
  toUserID: string,
  amount: string,
  reason: string,
  operationID: string,
  currency?: string,
) =>
  api<WalletTransferResult>(`/admin/wallets/transfer`, {
    method: "POST",
    body: {
      from_user_id: fromUserID,
      to_user_id: toUserID,
      amount,
      reason,
      operation_id: operationID,
      currency,
    },
  });

// Admin-initiated deposit / withdrawal lifecycle. The server prefixes
// the reason field with `[admin:<id>]` so the wallet detail panels
// can flag the row as operator-driven on subsequent reads.
export const adminCompleteDeposit = (depositID: string, reason: string) =>
  api(`/admin/deposits/${depositID}/complete`, { method: "POST", body: { reason } });
export const adminFailDeposit = (depositID: string, reason: string) =>
  api(`/admin/deposits/${depositID}/fail`, { method: "POST", body: { reason } });
export const adminCompleteWithdrawal = (withdrawalID: string, reason: string) =>
  api(`/admin/withdrawals/${withdrawalID}/complete`, { method: "POST", body: { reason } });
export const adminCancelWithdrawal = (withdrawalID: string, reason: string) =>
  api(`/admin/withdrawals/${withdrawalID}/cancel`, { method: "POST", body: { reason } });
export const adminFailWithdrawal = (withdrawalID: string, reason: string) =>
  api(`/admin/withdrawals/${withdrawalID}/fail`, { method: "POST", body: { reason } });

export const setUserStatus = (userID: string, status: string, reason = "") =>
  api(`/admin/users/${userID}/status`, { method: "POST", body: { status, reason } });
export const revokeUserSessions = (userID: string) =>
  api(`/admin/users/${userID}/sessions/revoke-all`, { method: "POST", body: {} });

// Scale only sends `replicas` — the engine-router resolves
// namespace + statefulset from its boot config (KUBE_NAMESPACE +
// ENGINE_STATEFULSET_NAME) so the operator UI doesn't have to know
// cluster topology. The previous shape `{ replicas }` failed silently
// because the server expected `{ namespace, statefulset, replicas }`;
// server-side defaults are now the source of truth.
export const scaleEngines = (replicas: number) =>
  api<{ replicas: number }>(`/admin/engine/scale`, {
    method: "POST",
    body: { replicas },
  });
export const rebalanceEngines = () =>
  api<RebalanceResult>(`/admin/engine/rebalance`, { method: "POST", body: {} });
export const drainPeer = (peerID: string) =>
  api(`/admin/engine/peers/${peerID}/drain`, { method: "POST" });
export const undrainPeer = (peerID: string) =>
  api(`/admin/engine/peers/${peerID}/undrain`, { method: "POST" });
export const restartPeer = (peerID: string) =>
  api(`/admin/engine/peers/${peerID}/restart`, { method: "POST" });
export const triggerEngineSync = () =>
  api(`/admin/engine/sync/trigger`, { method: "POST" });

export const setConfig = (key: string, value: unknown) =>
  api(`/admin/system/configs/${encodeURIComponent(key)}`, { method: "PUT", body: { value } });
export const deleteConfig = (key: string) =>
  api(`/admin/system/configs/${encodeURIComponent(key)}`, { method: "DELETE" });

// --- ranking / leagues (ranking-service via backoffice proxy) ---

// fetchUsersByIDs resolves a set of user ids to their profiles (the ignore
// list ships ids only; this hydrates display name / email / avatar for the
// chips). Reuses /admin/users?ids= (the same source the autocomplete uses).
export const fetchUsersByIDs = (ids: string[]) =>
  ids.length === 0
    ? Promise.resolve([] as UserItem[])
    : api<{ users: UserItem[]; page: PageInfo }>(
        `/admin/users${qs({ ids: ids.join(","), limit: 200 })}`,
      ).then((r) => r.users ?? []);

export const fetchIgnoreList = () =>
  api<{ items: IgnoreEntry[] }>("/admin/rank/ignore").then((r) => r.items ?? []);

export const updateIgnoreList = (userIDs: string[]) =>
  api<{ items: IgnoreEntry[] }>("/admin/rank/ignore", {
    method: "PUT",
    body: { user_ids: userIDs },
  }).then((r) => r.items ?? []);

// Per-broker global-leaderboard visibility (SM-774). Explicit rows only —
// a broker missing from the list is hidden.
export const fetchRankBrokerVisibility = () =>
  api<{ items: RankingBrokerVisibility[] }>("/admin/rank/brokers").then((r) => r.items ?? []);

export const updateRankBrokerVisibility = (brokerID: string, globalVisible: boolean) =>
  api<RankingBrokerVisibility>(`/admin/rank/brokers/${encodeURIComponent(brokerID)}/visibility`, {
    method: "PUT",
    body: { global_visible: globalVisible },
  });

export const fetchGlobalRanking = (opts: PageOpts & { metric: string }) =>
  api<RankBoard>(
    `/admin/rank/global${qs({
      metric: opts.metric,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  );

export const fetchLeaguesAdmin = (opts: PageOpts & { q?: string; status?: string }) =>
  api<{ items: AdminLeague[]; page: PageInfo }>(
    `/admin/leagues${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: opts.q,
      status: opts.status,
    })}`,
  ).then<Paginated<AdminLeague>>((r) => ({ items: r.items ?? [], page: r.page }));

export const fetchLeagueAdmin = (
  id: string,
  opts: { metric: string; limit: number; offset: number },
) =>
  api<AdminLeagueDetail>(
    `/admin/leagues/${id}${qs({
      metric: opts.metric,
      limit: opts.limit,
      offset: opts.offset,
    })}`,
  );

// --- commission reporting (SM-302 / SM-310) ---
//
// Every monetary field arrives as a STRING and is rendered as one.
// The API computes the authoritative totals; this layer never adds
// amounts up client-side — an int64 minor-unit total would lose
// precision the moment it passed through a JS number, and a UI that
// disagrees with the API about how much an admin earned is worse than
// one that shows nothing.

export interface CommissionTotals {
  gross_amount: string;
  net_amount: string;
  fills: number;
  commission_committed: string;
  commission_pending: string;
  commission_failed: string;
  committed_count: number;
  pending_count: number;
  failed_count: number;
  last_activity_at?: string;
}

export interface CommissionGroup extends CommissionTotals {
  /** event_id / market_id / admin_id per view. Empty = "No event". */
  key: string;
  market_count: number;
  recipient_count: number;
  /** Only set when recipient_count === 1; otherwise the row is mixed. */
  recipient_admin_id?: string;
  distinct_bps: number;
  /** The rate itself — present only on a single-rate group. */
  commission_bps?: number;
  /**
   * Display labels resolved by the server (SM-363). The browser cannot
   * resolve these itself: naming a poll needs catalog visibility and
   * naming an admin needs the directory, and My balance exists for a
   * CREATOR who holds neither. Only the label for this row's own kind
   * is sent, and only for a viewer entitled to it — an absent name is
   * normal, and the row falls back to its reference.
   */
  event_name?: string;
  market_name?: string;
  recipient_name?: string;
  /**
   * The event's child markets, when the request asked for them. Absent
   * (not empty) means "not expanded" — an empty array would claim the
   * event has no market activity, which is the bug this replaced.
   */
  markets?: CommissionGroup[];
}

export interface CommissionCharge {
  charge_id: string;
  // trade_id / order_id are empty on a REDEMPTION charge, which has no
  // fill behind it; redemption_id identifies it instead.
  trade_id: string;
  order_id: string;
  redemption_id?: string;
  market_id: string;
  event_id?: string;
  fee_amount: string;
  fee_role: string;
  commission_amount: string;
  commission_bps: number;
  commission_recipient_admin_id?: string;
  commission_source?: string;
  commission_status: string;
  commission_attempts: number;
  commission_last_error?: string;
  commission_next_attempt_at?: string;
  commission_waiting_reason?: string;
  ledger_operation_id?: string;
  platform_credit_operation_id?: string;
  platform_credit_status?: string;
  source_settlement_status?: string;
  created_at: string;
  /**
   * The channel dimension every charge carries (SM-456, SM-496).
   *
   * Two currencies, not one, because they denominate different columns:
   * `fee_amount` is in `currency` and `commission_amount` is in
   * `commission_currency`. Policy v1 makes them equal — a commission is
   * a slice of a fee already collected, so it is owed in that fee's
   * currency — but the wire states both, and a row renders each figure
   * with its OWN field rather than assuming the pair.
   *
   * Legacy rows arrive backfilled to GOLD/CASH rather than blank, so
   * nothing here has to guess a unit.
   */
  currency?: string;
  commission_currency?: string;
  trading_mode?: string;
  trading_channel_id?: string;
}

export interface CommissionDailyRow {
  day: string;
  gross_amount: string;
  commission_amount: string;
  net_amount: string;
  fills: number;
}

export type CommissionView = "event" | "market" | "recipient";

export interface CommissionFilters {
  date_from?: string;
  date_to?: string;
  recipient_admin_id?: string;
  event_id?: string;
  market_id?: string;
  status?: string;
  min_bps?: number;
  max_bps?: number;
  /**
   * Sent as the literal string "true" — qs() takes string|number, and
   * the API reads this as an exact string match.
   */
  commissioned_only?: "true";
  /**
   * Fee source (SM-327). A comma list the server ANDs into the one
   * shared predicate every commission read goes through, so the screen
   * and the CSV export cannot disagree about what they are counting:
   * "" (all) | "MAKER,TAKER" (trading) | "REDEMPTION".
   */
  fee_role?: string;
  /**
   * Which currency the report is denominated in (SM-496).
   *
   * Not an optional narrowing like the filters above it: an aggregate is
   * a SUM of int64 minor units, and RED, GREEN and GOLD share a wire
   * scale without sharing a value, so a total over a mix is not a number
   * (G8). Settlement pins an unscoped aggregate read to GOLD for exactly
   * that reason; sending the currency explicitly is what makes the pin
   * something an operator chose rather than something they inherited.
   *
   * Row-level reads (charges, and the CSV built from them) are NOT
   * pinned upstream — they carry per-row currency labels instead — so
   * passing it here is also what keeps the export scoped to the same
   * currency as the screen it was clicked from.
   */
  currency?: string;
}

/**
 * The presentation query for a grouped view (SM-383): which rows, in
 * what order, and which slice.
 *
 * Applied by backoffice, not by settlement — two of these cannot be
 * answered downstream. `q` matches event and recipient NAMES, which
 * settlement does not store, and `sort=event` orders by the label the
 * operator reads rather than by the id behind it. Both are resolved in
 * backoffice, so that is where the page boundary has to be cut too.
 */
export interface CommissionGroupQuery {
  /** Case-insensitive substring over event/market/recipient name and the
   *  reference shown on screen. */
  q?: string;
  sort?: "event" | "gross" | "commission" | "net" | "fills" | "last_activity";
  dir?: "asc" | "desc";
  /** 1-indexed. Sent instead of an offset so the wire reads the way the
   *  pager does. */
  page?: number;
  page_size?: number;
}

/**
 * A page of grouped rows. `page` is the standard console envelope, so
 * the shared <Pagination> renders this table with no special case.
 *
 * `total` is the count AFTER search — the number the pager divides.
 * `scanned` / `truncated` are separate on purpose: they say how much of
 * the underlying set was looked at, so "42 rows matched" can never be
 * confused with "we stopped looking".
 */
export interface CommissionGroupsPage {
  view: CommissionView;
  groups: CommissionGroup[];
  total: number;
  /**
   * The single currency every aggregate in `groups` is denominated in
   * (SM-456). Echoed by settlement and preserved through backoffice's
   * search/sort/page stage, so the table can label its columns with what
   * the SERVER scoped to rather than with what the client asked for —
   * the two differ for a caller that named no currency at all.
   */
  currency?: string;
  page: PageInfo;
  scanned?: number;
  truncated?: boolean;
}

export const commissionKeys = {
  all: ["admin-commissions"] as const,
  summary: (f: CommissionFilters) => [...commissionKeys.all, "summary", f] as const,
  groups: (view: CommissionView, f: CommissionFilters, g: CommissionGroupQuery) =>
    [...commissionKeys.all, "groups", view, f, g] as const,
  charges: (f: CommissionFilters, page: number) =>
    [...commissionKeys.all, "charges", f, page] as const,
  daily: (days: number, f: CommissionFilters) =>
    [...commissionKeys.all, "daily", days, f] as const,
  myEarnings: () => [...commissionKeys.all, "me"] as const,
  myGroups: (f: CommissionFilters, g: CommissionGroupQuery) =>
    [...commissionKeys.all, "me", "groups", f, g] as const,
};

/**
 * The totals strip, plus the currency context SM-456 added.
 *
 * `currency` is the ONE currency every flat field above is denominated
 * in — the requested scope, or settlement's GOLD pin when the caller
 * named none. `by_currency` is the additive full picture: one complete
 * set of totals per currency present in the filtered set, never summed
 * together. A screen shows the scoped strip and may LIST the others; it
 * must never add two of them.
 */
export interface CommissionCurrencyTotals extends CommissionTotals {
  currency: string;
}

export interface CommissionSummary extends CommissionTotals {
  currency?: string;
  by_currency?: CommissionCurrencyTotals[];
}

export const fetchCommissionSummary = (f: CommissionFilters = {}) =>
  api<CommissionSummary>(`/admin/commissions/summary${qs({ ...f })}`);

/**
 * Grouped commission rows.
 *
 * The event view asks for its children inline (SM-363). It cannot fetch
 * them on expansion instead: a viewer without catalog visibility is
 * shown an opaque reference in place of the event id, so "give me the
 * markets under event X" is a question it has no id to ask. Coming down
 * with the parent, the children are aggregated from the same rows and
 * reconcile with it exactly.
 */
export const fetchCommissionGroups = (
  view: CommissionView,
  f: CommissionFilters = {},
  g: CommissionGroupQuery = {},
) =>
  api<CommissionGroupsPage>(
    `/admin/commissions/groups${qs({
      ...f,
      ...listQueryParams(g),
      view,
      ...(view === "event" ? { include_children: "true" } : {}),
    })}`,
  );

/**
 * The shape every searched/sorted/paged console list sends. Named so
 * the serializer below can be shared by the tables that speak it —
 * they differ only in which column names their `sort` accepts.
 */
export interface ListPresentationQuery {
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

/**
 * qs() takes string|number, and an empty search must not be sent as
 * `q=` — the server would treat it as a predicate that matches
 * everything, which it does, but the query key would then differ from
 * the unsearched one and split the cache for no reason.
 *
 * `page` is omitted at 1 for the same reason: returning to the first
 * page must produce the SAME request the table opened with, or the reset
 * refetches what is already cached.
 */
function listQueryParams(g: ListPresentationQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (g.q) out.q = g.q;
  if (g.sort) out.sort = g.sort;
  if (g.dir) out.dir = g.dir;
  if (g.page && g.page > 1) out.page = g.page;
  if (g.page_size) out.page_size = g.page_size;
  return out;
}

export const fetchCommissionCharges = (
  f: CommissionFilters = {},
  opts: { limit?: number; offset?: number } = {},
) =>
  api<{ charges: CommissionCharge[]; total: number }>(
    `/admin/commissions/charges${qs({ ...f, ...opts })}`,
  );

export const fetchCommissionDaily = (days = 30, f: CommissionFilters = {}) =>
  api<{ days: CommissionDailyRow[] }>(
    `/admin/commissions/daily${qs({ ...f, days })}`,
  ).then((r) => r.days ?? []);

/**
 * Self-earnings. Ungated on the server — every admin can see what they
 * personally earned, including a CREATOR with no treasury access — and
 * scoped structurally to the authenticated actor, so there is nothing
 * to pass in.
 */
export const fetchMyEarnings = () =>
  api<CommissionTotals>("/admin/me/earnings");

/**
 * The rows behind those totals — My balance's "Where it came from"
 * table (SM-372).
 *
 * A SEPARATE endpoint from /admin/commissions/groups, not the same one
 * with a recipient filter attached. The report endpoint widens with
 * permission: an admin who also held treasury access was served every
 * recipient's rows under a heading that says "Paid to you". A filter
 * this client sends cannot fix that — whose money a view is about has
 * to be the server's decision, and here it is pinned to the
 * authenticated actor unconditionally.
 *
 * Same shape and the same `include_children` contract as the report, so
 * CommissionGroupTable renders either without knowing which it got.
 */
export const fetchMyCommissionGroups = (
  f: CommissionFilters = {},
  g: CommissionGroupQuery = {},
) =>
  api<CommissionGroupsPage>(
    `/admin/me/commissions/groups${qs({
      ...f,
      ...listQueryParams(g),
      view: "event",
      include_children: "true",
    })}`,
  );

/**
 * CSV export URL. Deliberately a plain link rather than a fetch: the
 * browser handles the download, Content-Disposition and all, and the
 * export carries exactly the filters on screen — the server applies
 * the same authorization to it as to the table above it.
 */
export const commissionExportURL = (f: CommissionFilters = {}) =>
  `/admin/commissions/export${qs({ ...f })}`;

/* ── commission CONFIGURATION (SM-304 endpoints, SM-321 UI) ───────── */

/**
 * One admin's baseline rate — the bottom of the resolution chain, and
 * the rate that follows them onto every poll they author unless an
 * event- or market-level override sits above it.
 *
 * `commission_bps` is an INTEGER (0…10000), never a decimal: see
 * @/lib/bps for the conversion. The API returns 0 bps rather than 404
 * for an admin with no stored row, so "never configured" and
 * "configured to zero" arrive identically — `updated_at` is what tells
 * them apart.
 */
export interface AdminCommissionDefault {
  admin_id: string;
  /**
   * The currency this policy is denominated in (SM-522) — the second
   * half of its identity, not a detail of it.
   *
   * A commission is a share of a fee the platform already collected, so
   * it is paid out of that currency's platform revenue into the admin's
   * account in the SAME currency. One admin therefore has up to one
   * policy per currency, each with its own rates, `config_version` and
   * audit history, and editing one cannot touch another.
   *
   * There is no row meaning "every currency". An admin with no Red
   * policy earns nothing on a Red fee, and that absence never reads as
   * the Gold rate.
   *
   * Optional only for the mixed-version window: a market-service from
   * before SM-522 omits it, and every row it returns is the Gold one.
   */
  currency?: CurrencyCode;
  /**
   * @deprecated since SM-387 — equals `taker_commission_bps`. Kept so a
   * response from a market-service that has not yet deployed SM-387
   * still renders a rate instead of a blank.
   */
  commission_bps: number;
  /**
   * The three independent shares (SM-387). Optional only for the
   * mixed-version window: `adminRates()` normalises them, and nothing
   * else in the app should read them raw.
   */
  maker_commission_bps?: number;
  taker_commission_bps?: number;
  redeem_commission_bps?: number;
  /** Echoed back as `expected_version` on the next save. */
  config_version?: number;
  /** False when no row exists — the rates read 0 but nobody chose them. */
  configured?: boolean;
  updated_by?: string;
  updated_at?: string;
}

/** One admin's three rates, in the order every surface renders them. */
export interface CommissionRates {
  maker: number;
  taker: number;
  redeem: number;
}

/**
 * The same three fee types, but as an OVERRIDE: null is "inherit", and
 * 0 is "earns nothing", and the two are opposite answers.
 */
export interface CommissionOverrideRates {
  maker: number | null;
  taker: number | null;
  redeem: number | null;
}

/**
 * Normalises a policy row into the three rates, falling back to the
 * legacy single rate when the server has not yet deployed SM-387.
 *
 * Every screen goes through here so the fallback exists in exactly one
 * place: a component reading `row.maker_commission_bps ?? 0` during the
 * rollout window would render a real 20% rate as 0%, which is
 * indistinguishable from a deliberate zero.
 */
export function adminRates(
  row: Pick<
    AdminCommissionDefault,
    | "commission_bps"
    | "maker_commission_bps"
    | "taker_commission_bps"
    | "redeem_commission_bps"
  > | null
  | undefined,
): CommissionRates {
  const legacy = row?.commission_bps ?? 0;
  return {
    maker: row?.maker_commission_bps ?? legacy,
    taker: row?.taker_commission_bps ?? legacy,
    redeem: row?.redeem_commission_bps ?? legacy,
  };
}

/** True when all three rates agree — rendered as one rate, not a breakdown. */
export function ratesAreUniform(r: CommissionRates): boolean {
  return r.maker === r.taker && r.taker === r.redeem;
}

/**
 * Which book a commission question is about (SM-489). A market can open
 * two, and every rate, source and lock state below is per book.
 *
 * The wire name is `trading_mode` — the database's own word — while the
 * value strings match the mode vocabulary the rest of the console uses
 * (TradingModePanel's MODE_LABEL renders them).
 */
export type CommissionMode = "CASH" | "GAME";

/**
 * The mode selector every per-node commission endpoint takes. Omitting
 * it is CASH server-side; sending it explicitly is what makes a request
 * readable in a network log next to its twin.
 */
const modeQS = (mode: CommissionMode) => `?trading_mode=${mode}`;

export const commissionConfigKeys = {
  all: ["admin-commission-config"] as const,
  // The currency is part of the KEY for the same reason the mode is
  // (SM-522): two currencies on one admin are two different answers, and
  // sharing a cache entry between them would show an operator another
  // currency's rate.
  adminDefault: (adminID: string, currency: CurrencyCode) =>
    [...commissionConfigKeys.all, "admin-default", adminID, currency] as const,
  adminDefaults: (currency?: CurrencyCode) =>
    [...commissionConfigKeys.all, "admin-defaults", currency ?? "all"] as const,
  // The mode is part of the KEY, not a detail inside the value: two
  // modes on one node are two different answers, and sharing a cache
  // entry between them would show an operator the other book's rate.
  event: (eventID: string, mode: CommissionMode = "CASH") =>
    [...commissionConfigKeys.all, "event", eventID, mode] as const,
  market: (marketID: string, mode: CommissionMode = "CASH") =>
    [...commissionConfigKeys.all, "market", marketID, mode] as const,
  effective: (marketID: string, mode: CommissionMode = "CASH") =>
    [...commissionConfigKeys.all, "effective", marketID, mode] as const,
};

/**
 * One admin's policy in ONE currency.
 *
 * The currency is required here even though the server defaults an
 * absent one to Gold: that default is a compatibility boundary for
 * clients written before SM-522, and a console that relied on it would
 * silently show the Gold rate on a screen the operator had filtered to
 * Red.
 */
export const fetchAdminCommissionDefault = (
  adminID: string,
  currency: CurrencyCode,
) =>
  api<AdminCommissionDefault>(
    `/admin/commission/admin-defaults/${encodeURIComponent(adminID)}` +
      `?currency=${encodeURIComponent(currency)}`,
  );

/**
 * Every admin who has a stored default. Admins with no row are absent
 * rather than present-with-zero, which is why the rates table joins this
 * against the admin list instead of rendering it directly — an admin
 * missing here earns the fallback, and that is worth showing.
 */
/**
 * Every stored policy, optionally narrowed to one currency.
 *
 * ABSENT currency means every currency, not Gold. After SM-522's
 * migration an admin can be configured in Gold and unconfigured in Red,
 * and a list that quietly showed Gold alone would report that admin as
 * settled when the decision an operator is looking for has not been
 * made.
 *
 * The filter runs server-side so a page boundary is drawn over the rows
 * the operator asked to see.
 */
export const fetchAdminCommissionDefaults = (currency?: CurrencyCode) =>
  api<{ admin_defaults: AdminCommissionDefault[] }>(
    "/admin/commission/admin-defaults" +
      (currency ? `?currency=${encodeURIComponent(currency)}` : ""),
  );

/**
 * An event-level override. `commission_bps` is null when the event has
 * none — nil and 0 are different answers (inherit vs. deliberately zero),
 * so the null survives all the way to the input field.
 */
export interface EventCommissionConfig {
  event_id: string;
  /** Which book this override prices. */
  trading_mode?: CommissionMode;
  commission_bps: number | null;
  maker_commission_bps: number | null;
  taker_commission_bps: number | null;
  redeem_commission_bps: number | null;
  config_version?: number;
  updated_by?: string;
  updated_at?: string;
}

export const fetchEventCommission = (
  eventID: string,
  mode: CommissionMode = "CASH",
) =>
  api<EventCommissionConfig>(
    `/admin/commission/events/${encodeURIComponent(eventID)}${modeQS(mode)}`,
  );

/**
 * `null` CLEARS this book's override; a number sets it. One call, one
 * book — sharing terms across both is two calls, which is what makes
 * "apply to both" a thing the operator did rather than a thing the
 * client inferred.
 */
export interface CommissionOverrideInput {
  maker_commission_bps?: number | null;
  taker_commission_bps?: number | null;
  redeem_commission_bps?: number | null;
}

export const setEventCommission = (
  eventID: string,
  input: CommissionOverrideInput,
  mode: CommissionMode = "CASH",
) =>
  api<EventCommissionConfig>(
    `/admin/commission/events/${encodeURIComponent(eventID)}${modeQS(mode)}`,
    { method: "PUT", body: input },
  );

/**
 * One market's mutable configuration plus its frozen snapshot.
 *
 * `locked` is the field that gates editing — NOT `is_published`. A market
 * can be unpublished again; its commission terms cannot, because they
 * were promised to a recipient at first publish.
 */
export interface MarketCommissionConfig {
  market_id: string;
  /** Which book this configuration and its lock belong to. */
  trading_mode?: CommissionMode;
  commission_bps: number | null;
  maker_commission_bps: number | null;
  taker_commission_bps: number | null;
  redeem_commission_bps: number | null;
  recipient_admin_id?: string;
  config_version?: string;
  locked?: boolean;
  locked_recipient_admin_id?: string;
  locked_bps?: number;
  /**
   * The three rates frozen at first publish (SM-387). Absent on a
   * market locked before the upgrade — read them through
   * `lockedRates()`, which falls back to `locked_bps` so a legacy
   * market shows the rate it actually promised.
   */
  locked_maker_bps?: number;
  locked_taker_bps?: number;
  locked_redeem_bps?: number;
  locked_source?: string;
  locked_version?: string;
  locked_at?: string;
}

export const fetchMarketCommission = (
  marketID: string,
  mode: CommissionMode = "CASH",
) =>
  api<MarketCommissionConfig>(
    `/admin/commission/markets/${encodeURIComponent(marketID)}${modeQS(mode)}`,
  );

/**
 * Sets a market's override. Both fields are tri-state on the wire and
 * therefore optional here:
 *
 *   commission_bps omitted → leave the override as it is
 *   commission_bps null    → clear it (inherit from event, then default)
 *   recipient_admin_id ""  → reset to the market's author
 */
export interface SetMarketCommissionInput {
  commission_bps?: number | null;
  maker_commission_bps?: number | null;
  taker_commission_bps?: number | null;
  redeem_commission_bps?: number | null;
  recipient_admin_id?: string;
}

export const setMarketCommission = (
  marketID: string,
  input: SetMarketCommissionInput,
  mode: CommissionMode = "CASH",
) =>
  api<MarketCommissionConfig>(
    `/admin/commission/markets/${encodeURIComponent(marketID)}${modeQS(mode)}`,
    { method: "PUT", body: input },
  );

/**
 * What this market WOULD pay if published now — or, once locked, what it
 * actually committed to. The server resolves the market → event → admin
 * chain, so the publish dialog never has to re-implement precedence.
 */
export interface EffectiveCommission {
  market_id: string;
  /** The book resolved. Two modes can answer with two different rates. */
  trading_mode?: CommissionMode;
  recipient_admin_id?: string;
  /** @deprecated since SM-387 — equals `taker_commission_bps`. */
  commission_bps: number;
  maker_commission_bps?: number;
  taker_commission_bps?: number;
  redeem_commission_bps?: number;
  /** MARKET | EVENT | ADMIN_DEFAULT | NONE — where the rate came from. */
  source: string;
  maker_source?: string;
  taker_source?: string;
  redeem_source?: string;
  currency_rates?: Array<{
    currency: string;
    collection_currency?: string;
    earning_currency?: string;
    maker_commission_bps: number;
    taker_commission_bps: number;
    redeem_commission_bps: number;
    source: string;
    maker_source?: string;
    taker_source?: string;
    redeem_source?: string;
  }>;
  locked: boolean;
  locked_at?: string;
  config_version?: string;
}

/**
 * The three rates a locked market froze, or the ones it would freeze if
 * published now. Same legacy fallback as `adminRates`, for the same
 * reason: during the rollout window a missing field is "this server
 * predates SM-387", never "0%".
 */
export function effectiveRates(
  e: EffectiveCommission | null | undefined,
): CommissionRates {
  const legacy = e?.commission_bps ?? 0;
  return {
    maker: e?.maker_commission_bps ?? legacy,
    taker: e?.taker_commission_bps ?? legacy,
    redeem: e?.redeem_commission_bps ?? legacy,
  };
}

/**
 * A node's stored OVERRIDE, per fee type — null where that fee type
 * inherits (SM-559).
 *
 * The legacy fallback is all-or-nothing, and deliberately not the
 * per-field `??` the other helpers use. `commission_bps` is the taker
 * mirror, so falling back field by field would read a post-SM-559 row
 * that overrides taker alone as overriding all three at taker's rate.
 * It is consulted only when the server sent none of the three — "this
 * server predates SM-559" — and never to fill a gap between them, where
 * an explicit null means the operator chose to inherit. Mirrors
 * `normalizedOverrides` in market-service, which decides the same
 * question on the way in.
 */
export function overrideRates(
  c:
    | {
        commission_bps?: number | null;
        maker_commission_bps?: number | null;
        taker_commission_bps?: number | null;
        redeem_commission_bps?: number | null;
      }
    | null
    | undefined,
): CommissionOverrideRates {
  if (!c) return { maker: null, taker: null, redeem: null };
  const explicit = {
    maker: c.maker_commission_bps,
    taker: c.taker_commission_bps,
    redeem: c.redeem_commission_bps,
  };
  if (
    explicit.maker === undefined &&
    explicit.taker === undefined &&
    explicit.redeem === undefined
  ) {
    const legacy = c.commission_bps ?? null;
    return { maker: legacy, taker: legacy, redeem: legacy };
  }
  return {
    maker: explicit.maker ?? null,
    taker: explicit.taker ?? null,
    redeem: explicit.redeem ?? null,
  };
}

/** True when any fee type carries an override — an explicit 0% included. */
export function hasOverrideRates(rates: CommissionOverrideRates): boolean {
  return rates.maker !== null || rates.taker !== null || rates.redeem !== null;
}

/** The rates a market locked at first publish. */
export function lockedRates(
  c: MarketCommissionConfig | null | undefined,
): CommissionRates {
  const legacy = c?.locked_bps ?? 0;
  return {
    maker: c?.locked_maker_bps ?? legacy,
    taker: c?.locked_taker_bps ?? legacy,
    redeem: c?.locked_redeem_bps ?? legacy,
  };
}

export const fetchEffectiveCommission = (
  marketID: string,
  mode: CommissionMode = "CASH",
) =>
  api<EffectiveCommission>(
    `/admin/commission/markets/${encodeURIComponent(marketID)}/effective${modeQS(mode)}`,
  );

/**
 * Sets the baseline rate. Affects only markets that publish AFTER this
 * call: commission terms are snapshotted and locked at the
 * RULE_PUBLISHED transition, so nothing already live re-prices.
 */
/**
 * A PARTIAL rate update (SM-387). Only the fee types present are
 * changed; the others are read from the stored row and rewritten
 * unchanged by market-service.
 *
 * Sending only what moved is what makes "editing one rate cannot change
 * the other two" true even when two operators save seconds apart — the
 * request literally says nothing about the fields it isn't editing.
 *
 * `expected_version` is the row's `config_version` as it was read. A
 * stale one answers 409 (MARKET_000901) instead of overwriting a rate
 * somebody else just set.
 */
export interface AdminCommissionRateUpdate {
  /**
   * REQUIRED (SM-522). The server refuses a write that does not name its
   * currency — unlike a read, where an absent one is a compatibility
   * default — because a rate saved into the wrong currency's policy is
   * money routed out of an account the operator never chose.
   */
  currency: CurrencyCode;
  maker_commission_bps?: number;
  taker_commission_bps?: number;
  redeem_commission_bps?: number;
  expected_version?: number;
}

export const setAdminCommissionDefault = (
  adminID: string,
  update: AdminCommissionRateUpdate,
) =>
  api<AdminCommissionDefault>(
    `/admin/commission/admin-defaults/${encodeURIComponent(adminID)}`,
    { method: "PUT", body: update },
  );

/* ── admin payouts (SM-308) ───────────────────────────────────────── */

/**
 * One payout request. `amount` is a STRING of canonical minor units:
 * a JSON number would arrive here as a float64 and lose precision on a
 * large balance, so nothing on this path parses it.
 */
export interface AdminWithdrawal {
  withdrawal_id: string;
  admin_id: string;
  amount: string;
  destination_address: string;
  asset_id: string;
  network_id: string;
  status: AdminWithdrawalStatus;
  /**
   * The platform currency debited from the requester's commission
   * account, and the one the payout settles in (SM-499).
   *
   * Neither is on the wire today: `admin_withdrawals` has no currency
   * column, the request debits the admin's GOLD commission account, and
   * SM-478 validates the rail's withdrawal currency to GOLD server-side
   * — a ceiling, not a switch. So an absent field is not unknown, it is
   * the legacy single currency (G3), which is how every other
   * pre-currency payload in this console is read.
   *
   * Declared anyway so the day the column exists the screen follows it
   * instead of continuing to apply a rule that has stopped being true.
   * `payoutCurrencies()` is the only thing that should read them.
   */
  source_currency?: string;
  settlement_currency?: string;
  /** Server-computed: whether this status still has funds in escrow. */
  holds_funds: boolean;
  /** The crypto rail's order id, set when the payout is handed off. */
  external_ref?: string;
  /**
   * The on-chain send. Empty until the rail reports the transaction —
   * a rejected, cancelled or not-yet-broadcast payout has none, and the
   * table shows a dash rather than borrowing `external_ref` (SM-351).
   */
  tx_hash?: string;

  /* Display fields folded on by backoffice-service (SM-351). ABSENT,
     not empty, when they could not be resolved — the crypto catalog was
     unreachable, or the payout never reached the rail. */

  /** Human network name, e.g. "KUB Testnet". */
  network_name?: string;
  /** Token symbol, e.g. "USDC". */
  asset_symbol?: string;
  /** The asset's own scale. A token amount rendered at the wrong one is
   *  a different number, so both travel together or neither does. */
  asset_decimals?: number;
  /** Gross token amount actually sent, in base units (decimal string).
   *  Known only once the rail priced the payout — the request itself is
   *  denominated in ⚡. */
  token_amount_base?: string;
  /** Withdrawal fee taken on-chain, base units. */
  token_fee_base?: string;
  /** The rail's order id, from the join rather than the request row. */
  crypto_order_id?: string;
  /** Explorer deep link for tx_hash, when the network has one. */
  explorer_url?: string;
  requested_by?: string;
  decided_by?: string;
  decided_at?: string;
  note?: string;
  last_error?: string;
  /**
   * Why an approval did not reach the crypto rail (SM-308 QA).
   *
   * Present ONLY on the response to a decision or a hand-off — it is not
   * stored on the request, because it describes this attempt rather than
   * the payout. Its presence means the approval stands but no send was
   * opened: either the rail refused the payout (in which case the escrow
   * has already gone back and `status` reads FAILED) or it could not be
   * reached (the request stays APPROVED and can be sent again).
   */
  dispatch_error?: string;
  created_at: string;
  updated_at: string;

  /* Who, by name (SM-308 QA). Resolved server-side from the admin
     roster and ABSENT when it could not be — an admin the roster no
     longer knows keeps the id rather than going blank, and an
     undecided request has no reviewer to name at all. */

  /** The requesting admin's display name, or their email. */
  admin_name?: string;
  /** The deciding reviewer's, once there is one. */
  decided_by_name?: string;
}

export interface AdminWithdrawalRequest {
  amount: string;
  destination_address: string;
  asset_id: string;
  network_id: string;
  note?: string;
}

/**
 * What a payout may be sent on (SM-350). A narrow projection of the
 * crypto catalog served from /me, so an admin with a commission balance
 * and no crypto permission can still fill in the destination.
 *
 * The server has already applied both filters — network live, asset
 * withdrawable — so everything here is selectable; the client only has
 * to keep the asset consistent with the chosen network.
 */
export interface WithdrawalOptionNetwork {
  network_id: string;
  name: string;
  standard: string;
  chain_id: number;
}

export interface WithdrawalOptionAsset {
  asset_id: string;
  network_id: string;
  symbol: string;
  kind: string;
  token_decimals: number;

  /* Pricing (SM-351). Absent when the asset has no active rate or no
     fee policy — the form then shows no conversion rather than a
     guessed one. */

  /** Active conversion rate, ⚡ ↔ token. */
  rate_num?: number;
  rate_den?: number;
  spark_minor_per_unit?: number;
  /** FLAT | PCT | GAS_PASSTHROUGH — how fee_value is applied. */
  fee_mode?: string;
  /** Base units for FLAT, basis points for PCT. */
  fee_value?: number;
  /** Per-withdrawal bounds, token base units. */
  min_base?: string;
  max_base?: string;
}

export interface WithdrawalOptions {
  networks: WithdrawalOptionNetwork[];
  assets: WithdrawalOptionAsset[];
}

/**
 * The seven states a payout can be in, in lifecycle order. Exported as
 * a value because the reviewer's filter menu iterates it — a filter
 * built from a hand-written list drifts from the type the moment a
 * state is added.
 */
export const ADMIN_WITHDRAWAL_STATUSES = [
  "PENDING",
  "APPROVED",
  "PROCESSING",
  "PAID",
  "FAILED",
  "REJECTED",
  "CANCELLED",
] as const;

export type AdminWithdrawalStatus = (typeof ADMIN_WITHDRAWAL_STATUSES)[number];

/**
 * What the reviewer's queue is narrowed to (SM-308 QA).
 *
 * `status` is a CSV in canonical order, not an array: it travels in the
 * URL and in the react-query key, and "PENDING,PAID" must not be a
 * different cache entry from "PAID,PENDING".
 *
 * `requester` — NOT admin_id. admin_id is the server's switch between
 * this queue and one admin's own history; sending it would change which
 * endpoint answered.
 */
export interface WithdrawalQueueFilter {
  status?: string;
  requester?: string;
  asset_id?: string;
  network_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

/**
 * The acting admin's own commission account (SM-373).
 *
 * Amounts are int64 minor-unit strings like every other money field
 * here — render them with <SparkAmount>, never as raw text.
 *
 * `available` is the ledger's, read whole — NOT commission_committed
 * minus what has been withdrawn. "Paid to you" is a lifetime total and
 * still counts money that has already left, so the two are different
 * numbers and the page must not compute one from the other.
 *
 * `provisioned` is false for an admin whose commission account has
 * never been created. The amounts are then true zeros; an unreachable
 * ledger is an error instead, never a zero.
 */
export interface MyBalance {
  available: string;
  reserved: string;
  pending_withdrawal: string;
  status?: string;
  provisioned: boolean;
}

/**
 * The presentation query for the caller's own payout history (SM-384).
 *
 * Answered by backoffice-service rather than by ledger, for the same
 * reason CommissionGroupQuery is: two of the columns it acts on — the
 * asset SYMBOL and the network NAME — are resolved by the display join,
 * so a search or an ordering applied upstream would be working on fields
 * that do not exist yet.
 */
export interface MyWithdrawalQuery {
  /** Case-insensitive substring over the address, transaction hash,
   *  asset, network, note and reference shown on the row. */
  q?: string;
  sort?: "requested" | "amount" | "asset" | "state";
  dir?: "asc" | "desc";
  /** 1-indexed. Sent instead of an offset so the wire reads the way the
   *  pager does. */
  page?: number;
  page_size?: number;
}

/**
 * A page of the caller's payouts. `page` is the standard console
 * envelope — including `has_more`, which backoffice adds because
 * ledger's own envelope omits it and the pager's Next button is disabled
 * without it.
 *
 * `total` is the count AFTER search — the number the pager divides and
 * the number the panel's badge claims. `scanned` / `truncated` are
 * separate on purpose: "3 payouts matched" must never be confused with
 * "we stopped looking".
 */
export interface MyWithdrawalsPage {
  withdrawals: AdminWithdrawal[];
  page: PageInfo;
  scanned?: number;
  truncated?: boolean;
}

export const payoutKeys = {
  all: ["admin-withdrawals"] as const,
  mine: (q: MyWithdrawalQuery = {}) => [...payoutKeys.all, "mine", q] as const,
  options: () => [...payoutKeys.all, "options"] as const,
  myBalance: () => [...payoutKeys.all, "my-balance"] as const,
  queue: (f: WithdrawalQueueFilter = {}) =>
    [
      ...payoutKeys.all,
      "queue",
      f.status ?? "",
      f.requester ?? "",
      f.asset_id ?? "",
      f.network_id ?? "",
      f.date_from ?? "",
      f.date_to ?? "",
      f.limit ?? 0,
      f.offset ?? 0,
    ] as const,
  one: (withdrawalID: string) => [...payoutKeys.all, "one", withdrawalID] as const,
};

export const fetchMyWithdrawalOptions = () =>
  api<WithdrawalOptions>("/admin/me/withdrawal-options");

/**
 * What is in the caller's commission account right now. Self-scoped on
 * the server from the session, so — like the rest of /admin/me/* —
 * there is deliberately no admin to pass in.
 */
export const fetchMyBalance = () => api<MyBalance>("/admin/me/balance");

/**
 * The caller's own payout history, searched, ordered and paged.
 *
 * The server pins admin_id from the session — there is deliberately no
 * parameter to ask for anyone else's, and the presentation query below
 * cannot widen it.
 */
export const fetchMyWithdrawals = (q: MyWithdrawalQuery = {}) =>
  api<MyWithdrawalsPage>(`/admin/me/withdrawals${qs(listQueryParams(q))}`);

export const requestMyWithdrawal = (body: AdminWithdrawalRequest) =>
  api<AdminWithdrawal>("/admin/me/withdrawals", { method: "POST", body });

export const cancelMyWithdrawal = (withdrawalID: string) =>
  api<AdminWithdrawal>(`/admin/me/withdrawals/${withdrawalID}/cancel`, {
    method: "POST",
  });

/**
 * The reviewer queue. Requires ADMIN_PAYOUT_REVIEW on the server.
 *
 * With no status filter the server returns only requests still holding
 * funds — the work to do — PENDING first and then oldest first. Naming
 * any status opts into history instead, which is how a reviewer answers
 * "what happened to that payout?".
 */
export const fetchWithdrawalQueue = (f: WithdrawalQueueFilter = {}) =>
  api<{ withdrawals: AdminWithdrawal[]; page: { total: number } }>(
    `/admin/admin-withdrawals${qs({
      status: f.status,
      requester: f.requester,
      asset_id: f.asset_id,
      network_id: f.network_id,
      date_from: f.date_from,
      date_to: f.date_to,
      limit: f.limit,
      offset: f.offset,
    })}`,
  );

/** One request, for the reviewer's detail view. */
export const fetchWithdrawal = (withdrawalID: string) =>
  api<AdminWithdrawal>(`/admin/admin-withdrawals/${withdrawalID}`);

/**
 * Approve or reject someone else's payout. `cancel` is deliberately not
 * accepted here — retracting a request belongs to its requester, and a
 * reviewer who wants it gone rejects it with a reason the requester can
 * read.
 */
export const decideWithdrawal = (
  withdrawalID: string,
  decision: "approve" | "reject",
  note?: string,
) =>
  api<AdminWithdrawal>(`/admin/admin-withdrawals/${withdrawalID}/decide`, {
    method: "POST",
    body: { decision, note },
  });

/**
 * Send an already-approved payout to the crypto rail (SM-308 QA).
 *
 * Approving dispatches on its own; this is the way out of "approved —
 * sending" when that call did not get through — the rail restarting, a
 * network blip. Idempotent all the way down: the rail resolves the send
 * it already opened rather than opening a second one, so a reviewer
 * clicking twice cannot pay an admin twice.
 */
export const dispatchWithdrawal = (withdrawalID: string) =>
  api<AdminWithdrawal>(`/admin/admin-withdrawals/${withdrawalID}/dispatch`, {
    method: "POST",
    body: {},
  });

// --- the RETIRED standalone redemption fee (SM-327, moved in SM-332) ---
//
// The live rate is a fee_profiles field now — see FeeProfile
// .redemption_fee_rate above — configured on the Fee policies page and
// scoped like maker and taker. What remains here is the read of the
// archive: redemptions taken before the move snapshotted versions from
// settlement-service's old table, and this feed is the only thing that
// says what one of those numbers meant.
//
// The rate is int64 minor units at rate_precision (6), the same
// convention as the trading fee profiles: minorToPercent /
// percentToMinor from FeeRateInput are the only place it converts.
// Money AMOUNTS are a different scale entirely (4dp, src/lib/money.ts)
// — crossing the two mis-scales by 10^4 in production money.

export interface RedemptionFeeConfig {
  config_id?: string;
  // Version 0 with no config_id is the never-configured state: no fee
  // has ever been set and redemptions credit the full payout. Distinct
  // from a deliberately-configured 0%, which carries a real version.
  version: number;
  mode: "PERCENT";
  rate: string;
  rate_precision: number;
  status?: "ACTIVE" | "ARCHIVED";
  note?: string;
  actor_admin_id?: string;
  actor_email?: string;
  activated_at?: string;
  archived_at?: string;
}

export const redemptionFeeKeys = {
  all: ["admin-redemption-fee"] as const,
  history: (limit: number, offset: number) =>
    [...redemptionFeeKeys.all, "history", limit, offset] as const,
};

export const fetchRedemptionFeeHistory = (opts: PageOpts) =>
  api<{ items: RedemptionFeeConfig[]; page: { total: number; limit: number; offset: number } }>(
    `/admin/fees/redemption/history${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({
    items: r.items ?? [],
    page: {
      total: r.page?.total ?? 0,
      limit: r.page?.limit ?? opts.limit,
      offset: r.page?.offset ?? opts.offset,
    },
  }));

// --- redemption attempts on one position (SM-327 support drill-down) ---
//
// The answer to "why did I receive less than the market showed": every
// attempt, the fee snapshot each was priced at, and why a failed one
// failed. Settlement has served this feed since the redeem flow
// shipped; until now nothing proxied it, so no operator could read it.

export interface RedemptionAttempt {
  redemption_id: string;
  position_id: string;
  settlement_id: string;
  user_id: string;
  idempotency_key: string;
  // payout_amount is the GROSS this attempt was raised against; what
  // the user received is net_amount. Read the explicit three, not this.
  payout_amount: string;
  fee_amount?: string;
  net_amount?: string;
  fee_mode?: string;
  fee_rate?: string;
  fee_rate_precision?: number;
  // 0 means no fee was configured when the position was redeemed —
  // distinct from a configured 0%, which carries a real version.
  fee_config_version?: number;
  ledger_op_id: string;
  ledger_tx_id?: string;
  status: "PENDING" | "COMMITTED" | "FAILED" | "ABANDONED";
  failure_reason?: string;
  attempt_no: number;
  started_at: string;
  completed_at?: string;
  // True when the auto-redeem executor raised this attempt rather than
  // the user clicking Redeem.
  auto?: boolean;
}

export const redemptionKeys = {
  all: ["admin-redemptions"] as const,
  byPosition: (positionID: string) =>
    [...redemptionKeys.all, "position", positionID] as const,
};

export const fetchPositionRedemptions = (positionID: string) =>
  api<{ redemptions: RedemptionAttempt[]; page: PageInfo }>(
    `/admin/positions/${positionID}/redemptions`,
  ).then((r) => r.redemptions ?? []);
